import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { useAuth } from '../AuthContext';
import {
  WORK_ORDER_BY_ID_QUERY,
  UPDATE_WORK_ORDER_STATUS_MUTATION,
  UPDATE_WORK_ORDER_LINE_ITEM_STATUS_MUTATION,
  UPDATE_WORK_ORDER_ASSIGNED_TO_MUTATION,
  ALL_USERS_QUERY,
} from '../graphql/workOrders';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import './QuoteDetail.css';
import PhotoGallery, { GalleryPhoto } from '../components/PhotoGallery';

const SERVER_URL = process.env.REACT_APP_API_HOST || 'http://localhost:8080';

interface LineItem {
  id: number;
  itemNumber: number;
  description: string;
  quantity: number;
  status: string;
  notes: string | null;
  draftQuoteObjectId: number | null;
}

interface WorkOrderImage {
  id: number;
  archivedAt: string | null;
  draftQuoteObjectImageByDraftQuoteObjectImageId: { id: number; storedFilename: string; originalFilename: string } | null;
  quoteImageByQuoteImageId: { id: number; storedFilename: string; originalFilename: string } | null;
}

interface WorkOrderData {
  id: number;
  description: string;
  travelerNumber: string | null;
  type: string | null;
  status: string;
  notes: string | null;
  designerClient: string | null;
  createdAt: string;
  dueAt: string | null;
  completedAt: string | null;
  quoteId: number;
  userByAssignedTo: { id: number; username: string } | null;
  quoteByQuoteId: {
    id: number;
    name: string;
    jobByJobId: { id: number; name: string; customerByCustomerId: { id: number; name: string } };
  };
  workOrderLineItemsByWorkOrderId: { nodes: LineItem[] };
  workOrderImagesByWorkOrderId: { nodes: WorkOrderImage[] };
}

const woStatusOptions = ['ready', 'in_progress', 'on_hold', 'complete'];

const woStatusColors: Record<string, { bg: string; text: string }> = {
  ready: { bg: '#f3f4f6', text: '#374151' },
  in_progress: { bg: '#dbeafe', text: '#1e40af' },
  on_hold: { bg: '#fef3c7', text: '#92400e' },
  complete: { bg: '#d1fae5', text: '#065f46' },
};

const liStatusColors: Record<string, { bg: string; text: string }> = {
  pending: { bg: '#f3f4f6', text: '#374151' },
  in_progress: { bg: '#dbeafe', text: '#1e40af' },
  completed: { bg: '#d1fae5', text: '#065f46' },
};

const liStatusNext: Record<string, string> = {
  pending: 'in_progress',
  in_progress: 'completed',
  completed: 'pending',
};

function parseNotes(notes: string | null): Record<string, string> {
  if (!notes) return {};
  const result: Record<string, string> = {};
  notes.split(' | ').forEach((part) => {
    const colonIdx = part.indexOf(': ');
    if (colonIdx > 0) {
      result[part.slice(0, colonIdx).trim()] = part.slice(colonIdx + 2).trim();
    }
  });
  return result;
}


export default function WorkOrderDetail() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const isWorker = user?.role === 'worker';
  const navigate = useNavigate();
  const location = useLocation();
  const backTo = (location.state as { from?: string } | null)?.from || '/work-orders';
  const backLabel = backTo === '/shop-status' ? 'Back to Shop Status' : 'Back to Work Orders';
  const [statusChanging, setStatusChanging] = useState(false);
  const [liChanging, setLiChanging] = useState<number | null>(null);
  const [reassigning, setReassigning] = useState(false);
  // Which print-only block window.print() picks up — the traveler sheet
  // (default, driven by the "Save as PDF" button) or the Photos block
  // (driven by "Print Photos"). Only one is ever rendered into the DOM at a
  // time so the browser's print output isn't the two concatenated together.
  const [printMode, setPrintMode] = useState<'traveler' | 'photos'>('traveler');

  useEffect(() => {
    const styleEl = document.createElement('style');
    styleEl.id = 'wo-landscape';
    styleEl.textContent = '@media print { @page { size: landscape; margin: 10mm; } }';
    document.head.appendChild(styleEl);
    return () => styleEl.remove();
  }, []);

  // Reverts to the traveler sheet after any print (photos included) so the
  // next "Save as PDF" click doesn't still be pointed at the photos block.
  useEffect(() => {
    const resetPrintMode = () => setPrintMode('traveler');
    window.addEventListener('afterprint', resetPrintMode);
    return () => window.removeEventListener('afterprint', resetPrintMode);
  }, []);

  function handlePrintPhotos() {
    setPrintMode('photos');
    // Give React a tick to render the photos print-only block before the
    // print dialog captures the DOM.
    setTimeout(() => window.print(), 50);
  }

  const { data, loading, refetch } = useQuery(WORK_ORDER_BY_ID_QUERY, {
    variables: { id: parseInt(id!) },
    skip: !id,
  });

  const { data: usersData } = useQuery(ALL_USERS_QUERY, {
    variables: { domainId: user?.domain_id || 0 },
    skip: !user?.domain_id || isWorker,
  });
  const assignees: { id: number; username: string; role: string }[] = usersData?.allUsers?.nodes || [];

  const [updateWoStatus] = useMutation(UPDATE_WORK_ORDER_STATUS_MUTATION);
  const [updateLiStatus] = useMutation(UPDATE_WORK_ORDER_LINE_ITEM_STATUS_MUTATION);
  const [updateAssignedTo] = useMutation(UPDATE_WORK_ORDER_ASSIGNED_TO_MUTATION);

  const wo: WorkOrderData | undefined = data?.workOrderById;
  const lineItems: LineItem[] = wo?.workOrderLineItemsByWorkOrderId?.nodes || [];
  const photos: GalleryPhoto[] = (wo?.workOrderImagesByWorkOrderId?.nodes || [])
    .filter((woImg) => !woImg.archivedAt)
    .map((woImg): GalleryPhoto | null => {
      if (woImg.draftQuoteObjectImageByDraftQuoteObjectImageId) {
        const img = woImg.draftQuoteObjectImageByDraftQuoteObjectImageId;
        return { key: `wo-img-${woImg.id}`, url: `${SERVER_URL}/uploads/object-images/${img.storedFilename}`, filename: img.originalFilename };
      }
      if (woImg.quoteImageByQuoteImageId) {
        const img = woImg.quoteImageByQuoteImageId;
        return { key: `wo-img-${woImg.id}`, url: `${SERVER_URL}/uploads/quote-images/${img.storedFilename}`, filename: img.originalFilename };
      }
      return null;
    })
    .filter((p): p is GalleryPhoto => p !== null);

  const handleWoStatusChange = async (newStatus: string) => {
    if (!wo || newStatus === wo.status) return;
    setStatusChanging(true);
    try {
      await updateWoStatus({ variables: { id: wo.id, status: newStatus } });
      await refetch();
    } finally {
      setStatusChanging(false);
    }
  };

  const handleReassign = async (newAssignedTo: string) => {
    if (!wo) return;
    setReassigning(true);
    try {
      await updateAssignedTo({ variables: { id: wo.id, assignedTo: newAssignedTo ? parseInt(newAssignedTo, 10) : null } });
      await refetch();
    } finally {
      setReassigning(false);
    }
  };

  const handleLiStatusAdvance = async (item: LineItem) => {
    setLiChanging(item.id);
    try {
      await updateLiStatus({ variables: { id: item.id, status: liStatusNext[item.status] || 'pending' } });
      await refetch();
    } finally {
      setLiChanging(null);
    }
  };

  const handleExportExcel = () => {
    if (!wo) return;
    const noteKeys: string[] = [];
    lineItems.forEach((item) => {
      Object.keys(parseNotes(item.notes)).forEach((k) => {
        if (!noteKeys.includes(k)) noteKeys.push(k);
      });
    });
    const csvEscape = (val: string | number) => {
      const str = String(val ?? '');
      return /[",\r\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
    };
    // Each line item gets its own two-column (label, value) block, offset by
    // 2 columns per item (item 1 → A/B, item 2 → D/E, item 3 → G/H, ...),
    // with a blank spacer column between blocks. Within a block, the item's
    // fields are printed 3 times, separated by a blank row, for tag/label cutting.
    const itemBlocks: [string, string | number][][] = lineItems.map((item) => {
      const parsed = parseNotes(item.notes);
      const fields: [string, string | number][] = [
        ['Item #', item.itemNumber],
        ['Room/Window', item.description],
        ['Quantity', Number(item.quantity)],
        ['Status', item.status.replace('_', ' ')],
        ...noteKeys.map((k): [string, string] => [k, parsed[k] || '']),
      ];
      const block: [string, string | number][] = [];
      for (let copy = 0; copy < 3; copy++) {
        fields.forEach((f) => block.push(f));
        block.push(['', '']);
      }
      return block;
    });
    const maxRows = Math.max(0, ...itemBlocks.map((b) => b.length));
    const csvRows: string[] = [];
    for (let r = 0; r < maxRows; r++) {
      const rowCells: string[] = [];
      itemBlocks.forEach((block, idx) => {
        const [label, value] = block[r] || ['', ''];
        rowCells.push(csvEscape(label), csvEscape(value));
        if (idx < itemBlocks.length - 1) rowCells.push('');
      });
      csvRows.push(rowCells.join(','));
    }
    const csv = csvRows.join('\r\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${wo.description || 'work-order'}-line-items.csv`.replace(/[^a-z0-9.\-_ ]/gi, '_');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (loading) return <div className="quote-detail-page">Loading...</div>;

  if (!wo) {
    return (
      <div className="quote-detail-page">
        <button className="back-button btn-icon" onClick={() => navigate(backTo)}>
          <ArrowBackIcon fontSize="small" /> {backLabel}
        </button>
        <div className="error-message">Work order not found</div>
      </div>
    );
  }

  const statusColors = woStatusColors[wo.status] || woStatusColors.ready;
  const completedCount = lineItems.filter((li) => li.status === 'completed').length;

  return (
    <div className="quote-detail-page work-order-page">
      <div className="print-only">
        {printMode === 'photos' ? (
          <PhotosPrintLayout wo={wo} photos={photos} />
        ) : (
          <WorkOrderPrintLayout wo={wo} lineItems={lineItems} />
        )}
      </div>
      <div className="no-print" style={{ display: 'contents' }}>
      <button className="back-button no-print btn-icon" onClick={() => navigate(backTo)}>
        <ArrowBackIcon fontSize="small" /> Back to Work Orders
      </button>
      <div className="no-print" style={{ float: 'right', marginBottom: 12, display: 'flex', gap: 8 }}>
        <button
          onClick={handleExportExcel}
          style={{ background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: 6, padding: '7px 14px', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}
        >
          Export to Excel
        </button>
        <button
          onClick={() => { setPrintMode('traveler'); window.print(); }}
          style={{ background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: 6, padding: '7px 14px', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}
        >
          Save as PDF
        </button>
      </div>

      <div className="detail-container" style={{ maxWidth: 1100 }}>
        {/* Traveler sheet header */}
        <div
          style={{
            border: '2px solid #374151',
            borderRadius: 6,
            marginBottom: 24,
            backgroundColor: 'white',
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          }}
        >
          {/* Top header row */}
          <div style={{ display: 'flex', borderBottom: '1px solid #374151' }}>
            <div style={s.hCell}>
              <div style={s.hLabel}>Assigned to:</div>
              {isWorker ? (
                <div style={s.hValue}>{wo.userByAssignedTo?.username || '—'}</div>
              ) : (
                <select
                  value={wo.userByAssignedTo?.id ?? ''}
                  disabled={reassigning}
                  onChange={(e) => handleReassign(e.target.value)}
                  style={{ ...s.hValue, border: 'none', outline: 'none', background: 'transparent', width: '100%', padding: 0, cursor: 'pointer' }}
                >
                  <option value="">Unassigned</option>
                  {assignees.map((a) => (
                    <option key={a.id} value={a.id}>{a.username}</option>
                  ))}
                </select>
              )}
            </div>
            <div style={{ ...s.hCell, flex: 2 }}>
              <div style={s.hLabel}>Designer/Client:</div>
              <div style={s.hValue}>{(wo.designerClient || wo.quoteByQuoteId.jobByJobId.customerByCustomerId.name)}</div>
            </div>
            <div style={s.hCell}>
              <div style={s.hLabel}>Date:</div>
              <div style={s.hValue}>{new Date(wo.createdAt).toLocaleDateString()}</div>
            </div>
            <div style={{ ...s.hCell, borderRight: 'none' }}>
              <div style={s.hLabel}>Install Date:</div>
              <div style={s.hValue}>{wo.dueAt ? new Date(wo.dueAt).toLocaleDateString() : '—'}</div>
            </div>
          </div>

          {/* Description + meta row */}
          <div style={{ display: 'flex', alignItems: 'center', padding: '14px 20px', gap: 20, flexWrap: 'wrap' }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 22, fontWeight: 700, color: '#111827' }}>{wo.description}</div>
              {wo.travelerNumber && (
                <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>Traveler #{wo.travelerNumber}</div>
              )}
            </div>
            <div className="no-print" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {!isWorker && (
                <span style={{ fontSize: 12, color: '#6b7280' }}>
                  Quote:{' '}
                  <span
                    style={{ color: '#3b82f6', cursor: 'pointer', textDecoration: 'underline' }}
                    onClick={() => navigate(`/quotes/${wo.quoteId}`)}
                  >
                    #{wo.quoteId} {wo.quoteByQuoteId.name}
                  </span>
                </span>
              )}
              <select
                value={wo.status}
                onChange={(e) => handleWoStatusChange(e.target.value)}
                disabled={statusChanging}
                style={{
                  padding: '5px 10px',
                  border: `2px solid ${statusColors.text}`,
                  borderRadius: 20,
                  backgroundColor: statusColors.bg,
                  color: statusColors.text,
                  fontWeight: 700,
                  fontSize: 12,
                  cursor: 'pointer',
                  textTransform: 'capitalize',
                }}
              >
                {woStatusOptions.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt.replace('_', ' ')}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Progress bar */}
          {lineItems.length > 0 && (
            <div className="no-print" style={{ padding: '0 20px 14px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#6b7280', marginBottom: 4 }}>
                <span>Progress</span>
                <span>{completedCount}/{lineItems.length} items complete</span>
              </div>
              <div style={{ height: 6, backgroundColor: '#e5e7eb', borderRadius: 3, overflow: 'hidden' }}>
                <div
                  style={{
                    height: '100%',
                    backgroundColor: '#10b981',
                    borderRadius: 3,
                    width: `${lineItems.length > 0 ? (completedCount / lineItems.length) * 100 : 0}%`,
                    transition: 'width 0.3s',
                  }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Line items — layout depends on work order type */}
        <div className="section">
          <h2>Line Items ({lineItems.length})</h2>

          {lineItems.length === 0 ? (
            <div className="empty-state">No line items on this work order.</div>
          ) : wo.type === 'roller_shade' ? (
            <RollerShadeItems items={lineItems} liChanging={liChanging} onAdvance={handleLiStatusAdvance} />
          ) : wo.type === 'pillow' ? (
            <PillowItems items={lineItems} liChanging={liChanging} onAdvance={handleLiStatusAdvance} />
          ) : wo.type === 'sham' ? (
            <ShamItems items={lineItems} liChanging={liChanging} onAdvance={handleLiStatusAdvance} />
          ) : wo.type === 'bolster_pillow' ? (
            <BolsterItems items={lineItems} liChanging={liChanging} onAdvance={handleLiStatusAdvance} />
          ) : wo.type === 'cushion' ? (
            <CushionItems items={lineItems} liChanging={liChanging} onAdvance={handleLiStatusAdvance} />
          ) : wo.type === 'dustskirt' ? (
            <DustskirtItems items={lineItems} liChanging={liChanging} onAdvance={handleLiStatusAdvance} />
          ) : wo.type === 'austrian_shade' ? (
            <AustrianShadeItems items={lineItems} liChanging={liChanging} onAdvance={handleLiStatusAdvance} />
          ) : wo.type === 'balloon_shade' ? (
            <BalloonShadeItems items={lineItems} liChanging={liChanging} onAdvance={handleLiStatusAdvance} />
          ) : wo.type === 'roman_shade' ? (
            <RomanShadeItems items={lineItems} liChanging={liChanging} onAdvance={handleLiStatusAdvance} />
          ) : wo.type === 'roman_valance' ? (
            <RomanValanceItems items={lineItems} liChanging={liChanging} onAdvance={handleLiStatusAdvance} />
          ) : wo.type === 'pleated_valance' || wo.type === 'tailored_valance' || wo.type === 'shirred_valance' ? (
            <PleatedValanceItems items={lineItems} liChanging={liChanging} onAdvance={handleLiStatusAdvance} />
          ) : wo.type === 'box_spring_cover' ? (
            <BoxSpringItems items={lineItems} liChanging={liChanging} onAdvance={handleLiStatusAdvance} />
          ) : wo.type === 'duvet' ? (
            <DuvetItems items={lineItems} liChanging={liChanging} onAdvance={handleLiStatusAdvance} />
          ) : wo.type === 'coverlet' ? (
            <CoverletItems items={lineItems} liChanging={liChanging} onAdvance={handleLiStatusAdvance} />
          ) : wo.type === 'tablecloth_round' ? (
            <TableclothRoundItems items={lineItems} liChanging={liChanging} onAdvance={handleLiStatusAdvance} />
          ) : wo.type === 'tablecloth_rectangle' ? (
            <TableclothRectangleItems items={lineItems} liChanging={liChanging} onAdvance={handleLiStatusAdvance} />
          ) : wo.type === 'bench' ? (
            <BenchItems items={lineItems} liChanging={liChanging} onAdvance={handleLiStatusAdvance} />
          ) : wo.type === 'bed' ? (
            <BedItems items={lineItems} liChanging={liChanging} onAdvance={handleLiStatusAdvance} />
          ) : wo.type === 'furniture' ? (
            <FurnitureItems items={lineItems} liChanging={liChanging} onAdvance={handleLiStatusAdvance} isWorker={isWorker} />
          ) : (
            <TravelerItems items={lineItems} liChanging={liChanging} onAdvance={handleLiStatusAdvance} />
          )}
        </div>

        {/* Photos */}
        {photos.length > 0 && (
          <div className="section">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h2>Photos ({photos.length})</h2>
              <button
                type="button"
                className="no-print"
                onClick={handlePrintPhotos}
                style={{ background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: 6, padding: '5px 12px', fontWeight: 600, fontSize: 12, cursor: 'pointer' }}
              >
                Print Photos
              </button>
            </div>
            <PhotoGallery photos={photos} size={140} />
          </div>
        )}

        {/* Notes */}
        {wo.notes && (
          <div className="section">
            <h2>Notes</h2>
            <div
              style={{
                padding: '14px 16px',
                backgroundColor: '#f9fafb',
                borderRadius: 6,
                fontSize: 14,
                color: '#374151',
                whiteSpace: 'pre-wrap',
                lineHeight: 1.6,
              }}
            >
              {wo.notes}
            </div>
          </div>
        )}

        {/* Meta info */}
        <div className="section">
          <h2>Details</h2>
          <div className="info-grid">
            <div className="info-row">
              <label>Quote</label>
              <div>
                <span
                  style={{ color: '#3b82f6', cursor: 'pointer', textDecoration: 'underline' }}
                  onClick={() => navigate(`/quotes/${wo.quoteId}`)}
                >
                  #{wo.quoteId} {wo.quoteByQuoteId.name}
                </span>
              </div>
            </div>
            <div className="info-row">
              <label>Customer</label>
              <div>{(wo.designerClient || wo.quoteByQuoteId.jobByJobId.customerByCustomerId.name)}</div>
            </div>
            <div className="info-row">
              <label>Created</label>
              <div>{new Date(wo.createdAt).toLocaleDateString()}</div>
            </div>
            {wo.completedAt && (
              <div className="info-row">
                <label>Completed</label>
                <div>{new Date(wo.completedAt).toLocaleDateString()}</div>
              </div>
            )}
          </div>
        </div>
      </div>
      </div>{/* end no-print */}
    </div>
  );
}

// ── Print layout — physical traveler sheet format ─────────────────────────────

const TRAVELER_COLS: { top: string; bot: string; key: string | null }[] = [
  { top: 'Fabric/',  bot: 'Color',     key: null },
  { top: '',         bot: 'Yds',       key: null },
  { top: '',         bot: 'Qty',       key: 'Qty' },
  { top: '#',        bot: 'Prs',       key: 'Prs' },
  { top: 'Width/',   bot: 'Panel',     key: 'Width/Panel' },
  { top: 'Rod',      bot: 'Face',      key: 'Rod Face' },
  { top: '',         bot: 'F.W.',      key: 'F.W.' },
  { top: '',         bot: 'F.L.',      key: 'F.L.' },
  { top: 'Hem',      bot: 'Size',      key: 'Hem' },
  { top: '',         bot: 'Returns',   key: 'Returns' },
  { top: 'Over',     bot: 'laps',      key: 'Overlaps' },
  { top: '',         bot: 'Line',      key: 'Line' },
  { top: 'Inter-',   bot: 'Line',      key: 'Inter-Line' },
  { top: 'Room/',    bot: 'Window',    key: 'Room/Window' },
  { top: '',         bot: 'Hardware',  key: 'Hardware' },
  { top: 'Pleat',    bot: 'Type',      key: 'Pleat Type' },
];

const cell: React.CSSProperties = { border: '1px solid #333', padding: '3pt 5pt', fontSize: '9pt', textAlign: 'center', verticalAlign: 'middle' };
const hCell: React.CSSProperties = { ...cell, fontSize: '7.5pt', fontWeight: 600, lineHeight: 1.2, verticalAlign: 'bottom' };

const ROLLER_PRINT_FIELDS: [string, string][] = [
  ['Window Width',   'WINDOW WIDTH'],
  ['Window Length',  'WINDOW LENGTH'],
  ['Tube Size',      'TUBE SIZE'],
  ['Fabric',         'FABRIC'],
  ['Drive',          'DRIVE'],
  ['Operating System', 'OPERATING SYSTEM'],
  ['Roll Direction', 'ROLL DIRECTION'],
  ['RT or LT',       'RT or LT'],
  ['Inside/Outside', 'Inside/Outside'],
];

// Per Quoting_markdown/pillows.md's "Work Order Outputs" — deliberately not
// the drapery/roman-shade traveler box layout, just these fields.
const PILLOW_PRINT_FIELDS: [string, string][] = [
  ['Size',   'SIZE'],
  ['Zipper', 'ZIPPER'],
  ['Insert', 'INSERT'],
  ['Fabric', 'FABRIC'],
  ['Welt/Trim/Ruffle', 'WELT/TRIM/RUFFLE'],
  ['Contrast Welt Fabric', 'CONTRAST WELT FABRIC'],
  ['Trim Name', 'TRIM NAME'],
];

// Per Quoting_markdown/shams.md's "Work Order Outputs" — identical wording
// to pillow's, same 5 fields.
const SHAM_PRINT_FIELDS: [string, string][] = [
  ['Size',   'SIZE'],
  ['Zipper', 'ZIPPER'],
  ['Insert', 'INSERT'],
  ['Fabric', 'FABRIC'],
  ['Welt/Trim/Ruffle', 'WELT/TRIM/RUFFLE'],
  ['Contrast Welt Fabric', 'CONTRAST WELT FABRIC'],
  ['Trim Name', 'TRIM NAME'],
];

// Per Quoting_markdown/bolster_pillows.md's "Work Order Outputs" — identical
// wording to pillow's/sham's, same 5 fields.
const BOLSTER_PRINT_FIELDS: [string, string][] = [
  ['Size',   'SIZE'],
  ['Zipper', 'ZIPPER'],
  ['Insert', 'INSERT'],
  ['Fabric', 'FABRIC'],
  ['Welt/Trim/Ruffle', 'WELT/TRIM/RUFFLE'],
  ['Contrast Welt Fabric', 'CONTRAST WELT FABRIC'],
  ['Trim Name', 'TRIM NAME'],
];

// Per Quoting_markdown/cushions.md's "Work Order Outputs" — same 5 fields as
// pillow/sham/bolster minus Ruffle (cushion's SOP has no Ruffle option).
const CUSHION_PRINT_FIELDS: [string, string][] = [
  ['Size',   'SIZE'],
  ['Zipper', 'ZIPPER'],
  ['Insert', 'INSERT'],
  ['Fabric', 'FABRIC'],
  ['Welt/Trim', 'WELT/TRIM'],
];

// Per Quoting_markdown/dustskirts.md's "Work Order Outputs" — Quantity, Bed
// Size, Style, Fabric, and a Drop box; "does not use the standard
// drapery/roman shade traveler format."
const DUSTSKIRT_PRINT_FIELDS: [string, string][] = [
  ['Bed Size', 'BED SIZE'],
  ['Style',    'STYLE'],
  ['Fabric',   'FABRIC'],
  ['Drop',     'DROP'],
];

// Per Quoting_markdown/benches.md — Bench has its own dedicated fields
// (family, shape, style, storage, legs, trim, nailheads) that have no
// business appearing on the generic drapery traveler. Keys match
// benchRowToArgs's pipe-delimited notes (CreateWorkOrder.tsx) exactly.
const BENCH_PRINT_FIELDS: [string, string][] = [
  ['Family',    'FAMILY'],
  ['Shape',     'SHAPE'],
  ['Style',     'STYLE'],
  ['Size',      'SIZE (W×D×H)'],
  ['Storage',   'STORAGE'],
  ['Legs',      'LEGS'],
  ['Trim',      'TRIM'],
  ['Nailheads', 'NAILHEADS'],
  ['Fabric',    'FABRIC'],
];

// Per Quoting_markdown/beds.md — Bed has its own dedicated fields (config,
// style, headboard size, edge treatment, mattress size, frame thickness,
// side rail/footboard height & style, drawers, trundle, feet, bunky boards)
// that have no business appearing on the generic drapery traveler. Keys
// match bedRowToArgs's pipe-delimited notes (CreateWorkOrder.tsx) exactly.
// Side Rail/Footboard Style is surfaced here per the SOP's explicit "shown
// on both the quote and work order" requirement.
const BED_PRINT_FIELDS: [string, string][] = [
  ['Config',                      'CONFIG'],
  ['Style',                       'STYLE'],
  ['Headboard Size',              'HEADBOARD SIZE'],
  ['Edge Treatment',              'EDGE TREATMENT'],
  ['Mattress Size',               'MATTRESS SIZE'],
  ['Frame Thickness',             'FRAME THICKNESS'],
  ['Side Rail/Footboard Height',  'SR/FB HEIGHT'],
  ['Side Rail/Footboard Style',   'SR/FB STYLE'],
  ['Drawers',                     'DRAWERS'],
  ['Trundle',                     'TRUNDLE'],
  ['Feet',                        'FEET'],
  ['Bunky Boards',                'BUNKY BOARDS'],
  ['Fabric',                      'FABRIC'],
];

// No shop SOP exists for furniture yet — this tab exists purely so the build
// spec collected on the draft object (DraftQuoteDetail.tsx's FurnitureForm)
// round-trips onto the traveler. Keys match furnitureRowToArgs's
// pipe-delimited notes (CreateWorkOrder.tsx) exactly.
const FURNITURE_PRINT_FIELDS: [string, string][] = [
  ['Type',     'TYPE'],
  ['Size',     'SIZE (W×D×H)'],
  ['Seat',     'SEAT'],
  ['Arms',     'ARMS'],
  ['Back',     'BACK'],
  ['Legs',     'LEGS'],
  ['Cushions', 'CUSHIONS'],
  ['Deck',     'DECK'],
  ['Fabric',   'FABRIC'],
];

// Austrian Shade — split off the shared Austrian/Balloon "ShadeRow" builder
// layout (CreateWorkOrder.tsx) into its own dedicated traveler tab, matching
// the Pillow/Sham/Dustskirt precedent. Note keys match shadeRowToArgs's
// pipe-delimited notes exactly. balloon_shade is left on the generic
// TravelerItems/print layout — untouched by this change.
const AUSTRIAN_SHADE_PRINT_FIELDS: [string, string][] = [
  ['Width',       'WIDTH'],
  ['Length',      'LENGTH'],
  ['Mount',       'MOUNT'],
  ['Fabric',      'FABRIC'],
  ['Lining',      'LINING'],
  ['Interlining', 'INTERLINING'],
  ['Trim',        'TRIM'],
  ['Operating System', 'OPERATING SYSTEM'],
  ['Board',       'BOARD'],
  ['Returns',     'RETURNS'],
];

// Per Quoting_markdown/box_spring_covers.md's "Work Order Outputs" — Bed
// Size, Fabric, and a blank Box Spring L/W/H box for the installer.
// Balloon Shade shares the identical shadeRowToArgs notes shape as Austrian
// Shade (CreateWorkOrder.tsx) — same field set, own print component so it's
// no longer misrouted onto the drapery-shaped generic layout.
const BALLOON_SHADE_PRINT_FIELDS: [string, string][] = AUSTRIAN_SHADE_PRINT_FIELDS;

// Pleated/Tailored/Shirred Valance still explode into one row per
// fabric-type draft_quote_object_item (objectToTravelerRows in
// CreateWorkOrder.tsx); Roman Shade/Valance use the same mechanism but
// collapse down to Fabric + Trim rows only. Unlike Austrian/Balloon Shade's
// single-row-per-object shape, these types reuse the Fabric/Color + Yds
// column layout (TRAVELER_COLS) rather than Austrian Shade's per-object card
// layout, with their own notes-key column lists matching travelerRowToArgs
// exactly.
const ROMAN_SHADE_COLS: { top: string; bot: string; key: string | null }[] = [
  { top: 'Fabric/', bot: 'Color',      key: null },
  { top: '',        bot: 'Yds',        key: null },
  { top: '',        bot: 'Qty',        key: 'Qty' },
  { top: '',        bot: '# Prs',      key: 'Prs' },
  { top: '',        bot: 'F.W.',       key: 'F.W.' },
  { top: '',        bot: 'F.L.',       key: 'F.L.' },
  { top: '',        bot: 'Line',       key: 'Line' },
  { top: 'Inter-',  bot: 'Line',       key: 'Inter-Line' },
  { top: 'Room/',   bot: 'Window',     key: 'Room/Window' },
  { top: 'Operating', bot: 'System',   key: 'Operating System' },
  { top: '',        bot: 'Board',      key: 'Board' },
  { top: '',        bot: 'Returns',    key: 'Returns' },
  { top: '',        bot: 'Flap',       key: 'Flap' },
];

// Roman Valance has no operating system/motor line at all (see CLAUDE.md),
// so no Motor column — otherwise identical to Roman Shade's column set.
const ROMAN_VALANCE_COLS: { top: string; bot: string; key: string | null }[] = [
  { top: 'Fabric/', bot: 'Color',      key: null },
  { top: '',        bot: 'Yds',        key: null },
  { top: '',        bot: 'Qty',        key: 'Qty' },
  { top: '',        bot: '# Prs',      key: 'Prs' },
  { top: '',        bot: 'F.W.',       key: 'F.W.' },
  { top: '',        bot: 'F.L.',       key: 'F.L.' },
  { top: '',        bot: 'Line',       key: 'Line' },
  { top: 'Inter-',  bot: 'Line',       key: 'Inter-Line' },
  { top: 'Room/',   bot: 'Window',     key: 'Room/Window' },
  { top: '',        bot: 'Board',      key: 'Board' },
  { top: '',        bot: 'Returns',    key: 'Returns' },
  { top: '',        bot: 'Flap',       key: 'Flap' },
];

// Pleated Valance has Returns instead of Flap, and (like Roman Valance) no
// Motor column. Reused verbatim for Tailored Valance and Shirred Valance,
// same as CreateWorkOrder.tsx's pleatedValanceTravelerCalc.
const PLEATED_VALANCE_COLS: { top: string; bot: string; key: string | null }[] = [
  { top: 'Fabric/', bot: 'Color',      key: null },
  { top: '',        bot: 'Yds',        key: null },
  { top: '',        bot: 'Qty',        key: 'Qty' },
  { top: '',        bot: 'F.W.',       key: 'F.W.' },
  { top: '',        bot: 'F.L.',       key: 'F.L.' },
  { top: '',        bot: 'Line',       key: 'Line' },
  { top: 'Inter-',  bot: 'Line',       key: 'Inter-Line' },
  { top: 'Room/',   bot: 'Window',     key: 'Room/Window' },
  { top: '',        bot: 'Board',      key: 'Board' },
  { top: '',        bot: 'Returns',    key: 'Returns' },
];

function ShadeTravelerPrintLayout({ wo, lineItems, cols }: { wo: WorkOrderData; lineItems: LineItem[]; cols: { top: string; bot: string; key: string | null }[] }) {
  return (
    <div style={{ fontFamily: 'Arial, Helvetica, sans-serif', padding: '12pt 14pt', fontSize: '9pt' }}>
      <PrintHeader wo={wo} />
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            {cols.map((col, i) => (
              <th key={i} style={hCell}>
                {col.top && <div>{col.top}</div>}
                <div>{col.bot}</div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {lineItems.map((item) => {
            const p = parseNotes(item.notes);
            return (
              <tr key={item.id}>
                <td style={{ ...cell, textAlign: 'left' }}>{item.description}</td>
                <td style={cell}>{Number(item.quantity) % 1 === 0 ? Number(item.quantity) : Number(item.quantity).toFixed(2)}</td>
                {cols.slice(2).map((col, i) => (
                  <td key={i} style={cell}>{col.key ? (p[col.key] || '') : ''}</td>
                ))}
              </tr>
            );
          })}
          {wo.notes && (
            <tr>
              <td style={{ ...cell, height: '36pt' }} />
              <td style={{ ...cell, height: '36pt' }} />
              <td colSpan={cols.length - 2} style={{ ...cell, textAlign: 'left', verticalAlign: 'top', padding: '5pt 6pt', lineHeight: 1.5 }}>
                {wo.notes}
              </td>
            </tr>
          )}
          <tr>
            <td style={{ ...cell, height: '40pt' }} />
            <td style={{ ...cell, height: '40pt' }} />
            <td colSpan={cols.length - 2} style={{ ...cell, height: '40pt' }} />
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function RomanShadePrintLayout({ wo, lineItems }: { wo: WorkOrderData; lineItems: LineItem[] }) {
  return <ShadeTravelerPrintLayout wo={wo} lineItems={lineItems} cols={ROMAN_SHADE_COLS} />;
}

function RomanValancePrintLayout({ wo, lineItems }: { wo: WorkOrderData; lineItems: LineItem[] }) {
  return <ShadeTravelerPrintLayout wo={wo} lineItems={lineItems} cols={ROMAN_VALANCE_COLS} />;
}

function PleatedValancePrintLayout({ wo, lineItems }: { wo: WorkOrderData; lineItems: LineItem[] }) {
  return <ShadeTravelerPrintLayout wo={wo} lineItems={lineItems} cols={PLEATED_VALANCE_COLS} />;
}

function BalloonShadePrintLayout({ wo, lineItems }: { wo: WorkOrderData; lineItems: LineItem[] }) {
  return (
    <div style={{ fontFamily: 'Arial, Helvetica, sans-serif', padding: '12pt 14pt', fontSize: '9pt' }}>
      <PrintHeader wo={wo} />
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <colgroup>
          <col style={{ width: '7%' }} />
          <col style={{ width: '16%' }} />
          <col style={{ width: '26%' }} />
          <col style={{ width: '36%' }} />
          <col style={{ width: '15%' }} />
        </colgroup>
        <thead>
          <tr>
            <td style={{ ...rc, border: 'none' }} />
            <td colSpan={4} style={{ ...rc, fontSize: '10pt', fontWeight: 600, paddingBottom: '6pt' }}>
              Inputs for balloon shade workorder
            </td>
          </tr>
        </thead>
        <tbody>
          {lineItems.map((item, idx) => {
            const p = parseNotes(item.notes);
            return (
              <React.Fragment key={item.id}>
                {idx > 0 && (
                  <tr><td colSpan={5} style={{ height: '10pt', border: 'none' }} /></tr>
                )}
                <tr>
                  <td style={{ ...rc, textAlign: 'right', fontWeight: 700, verticalAlign: 'middle' }}>{idx + 1}</td>
                  <td style={{ ...rc, border: 'none' }} />
                  <td style={{ ...rc, textAlign: 'right', textTransform: 'uppercase', fontWeight: 600, color: '#333' }}>Quantity</td>
                  <td style={{ ...rc }}>{Number(item.quantity)}</td>
                  <td style={{ ...rc, border: 'none' }} />
                </tr>
                {BALLOON_SHADE_PRINT_FIELDS.map(([noteKey, label]) => (
                  <tr key={noteKey}>
                    <td style={{ ...rc, border: 'none' }} />
                    <td style={{ ...rc, border: 'none' }} />
                    <td style={{ ...rc, textAlign: 'right', textTransform: 'uppercase', fontWeight: 600, color: '#333' }}>{label}</td>
                    <td style={{ ...rc }}>{p[noteKey] || ''}</td>
                    <td style={{ ...rc, border: 'none' }} />
                  </tr>
                ))}
              </React.Fragment>
            );
          })}
          {wo.notes && (
            <tr>
              <td colSpan={5} style={{ ...rc, paddingTop: '10pt', color: '#444' }}>{wo.notes}</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

const BOX_SPRING_PRINT_FIELDS: [string, string][] = [
  ['Bed Size', 'BED SIZE'],
  ['Fabric',   'FABRIC'],
  ['L/W/H',    'BOX SPRING L/W/H'],
];

// Per Quoting_markdown/duvets.md's "Work Order Outputs" — Size, Fabric,
// Self Welt (yes/no).
const DUVET_PRINT_FIELDS: [string, string][] = [
  ['Size',      'SIZE'],
  ['Fabric',    'FABRIC'],
  ['Self Welt', 'SELF WELT'],
];

// Per Quoting_markdown/coverlets.md's "Work Order Outputs" — Size, Fabric.
const COVERLET_PRINT_FIELDS: [string, string][] = [
  ['Size',   'SIZE'],
  ['Fabric', 'FABRIC'],
];

// Per Quoting_markdown/tablecloths.md's "Work Order Outputs" — Size of
// table, Fabric. Round and Rectangle share the same field set.
const TABLECLOTH_ROUND_PRINT_FIELDS: [string, string][] = [
  ['Size',   'SIZE'],
  ['Fabric', 'FABRIC'],
];

const TABLECLOTH_RECTANGLE_PRINT_FIELDS: [string, string][] = [
  ['Size',   'SIZE'],
  ['Fabric', 'FABRIC'],
];

const rc: React.CSSProperties = { border: '1px solid #bbb', padding: '4pt 6pt', fontSize: '9pt' };

function PrintHeader({ wo }: { wo: WorkOrderData }) {
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '10pt' }}>
      <tbody>
        <tr>
          <td style={{ ...cell, textAlign: 'left', width: '22%' }}>
            <div style={{ fontSize: '7.5pt', color: '#555' }}>Assigned to:</div>
            <div style={{ fontWeight: 600 }}>{wo.userByAssignedTo?.username || '—'}</div>
          </td>
          <td style={{ ...cell, textAlign: 'left', width: '34%' }}>
            <div style={{ fontSize: '7.5pt', color: '#555' }}>Designer/Client:</div>
            <div style={{ fontWeight: 600 }}>{(wo.designerClient || wo.quoteByQuoteId.jobByJobId.customerByCustomerId.name)}</div>
          </td>
          <td style={{ ...cell, textAlign: 'left', width: '22%' }}>
            <div style={{ fontSize: '7.5pt', color: '#555' }}>Date:</div>
            <div style={{ fontWeight: 600 }}>{new Date(wo.createdAt).toLocaleDateString()}</div>
          </td>
          <td style={{ ...cell, textAlign: 'left', width: '22%' }}>
            <div style={{ fontSize: '7.5pt', color: '#555' }}>Install Date:</div>
            <div style={{ fontWeight: 600 }}>{wo.dueAt ? new Date(wo.dueAt).toLocaleDateString() : '—'}</div>
          </td>
        </tr>
        <tr>
          <td colSpan={4} style={{ ...cell, textAlign: 'left', fontWeight: 700, fontSize: '11pt', padding: '6pt 8pt' }}>
            {wo.description}{wo.travelerNumber ? ` — T#${wo.travelerNumber}` : ''}
          </td>
        </tr>
      </tbody>
    </table>
  );
}

function RollerShadePrintLayout({ wo, lineItems }: { wo: WorkOrderData; lineItems: LineItem[] }) {
  return (
    <div style={{ fontFamily: 'Arial, Helvetica, sans-serif', padding: '12pt 14pt', fontSize: '9pt' }}>
      <PrintHeader wo={wo} />
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <colgroup>
          <col style={{ width: '7%' }} />
          <col style={{ width: '16%' }} />
          <col style={{ width: '26%' }} />
          <col style={{ width: '36%' }} />
          <col style={{ width: '15%' }} />
        </colgroup>
        <thead>
          <tr>
            <td style={{ ...rc, border: 'none' }} />
            <td colSpan={4} style={{ ...rc, fontSize: '10pt', fontWeight: 600, paddingBottom: '6pt' }}>
              Inputs for roller shade workorder
            </td>
          </tr>
        </thead>
        <tbody>
          {lineItems.map((item, idx) => {
            const p = parseNotes(item.notes);
            return (
              <React.Fragment key={item.id}>
                {/* blank separator row between shades */}
                {idx > 0 && (
                  <tr><td colSpan={5} style={{ height: '10pt', border: 'none' }} /></tr>
                )}
                {/* ROOM/WINDOW row — shows shade number */}
                <tr>
                  <td style={{ ...rc, textAlign: 'right', fontWeight: 700, verticalAlign: 'middle' }}>{idx + 1}</td>
                  <td style={{ ...rc, border: 'none' }} />
                  <td style={{ ...rc, textAlign: 'right', textTransform: 'uppercase', fontWeight: 600, color: '#333' }}>Room/Window</td>
                  <td style={{ ...rc }}>{item.description}</td>
                  <td style={{ ...rc, border: 'none' }} />
                </tr>
                {ROLLER_PRINT_FIELDS.map(([noteKey, label]) => (
                  <tr key={noteKey}>
                    <td style={{ ...rc, border: 'none' }} />
                    <td style={{ ...rc, border: 'none' }} />
                    <td style={{ ...rc, textAlign: 'right', textTransform: 'uppercase', fontWeight: 600, color: '#333' }}>{label}</td>
                    <td style={{ ...rc }}>{p[noteKey] || ''}</td>
                    <td style={{ ...rc, border: 'none' }} />
                  </tr>
                ))}
              </React.Fragment>
            );
          })}
          {/* Notes */}
          {wo.notes && (
            <tr>
              <td colSpan={5} style={{ ...rc, paddingTop: '10pt', color: '#444' }}>{wo.notes}</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function PillowPrintLayout({ wo, lineItems }: { wo: WorkOrderData; lineItems: LineItem[] }) {
  return (
    <div style={{ fontFamily: 'Arial, Helvetica, sans-serif', padding: '12pt 14pt', fontSize: '9pt' }}>
      <PrintHeader wo={wo} />
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <colgroup>
          <col style={{ width: '7%' }} />
          <col style={{ width: '16%' }} />
          <col style={{ width: '26%' }} />
          <col style={{ width: '36%' }} />
          <col style={{ width: '15%' }} />
        </colgroup>
        <thead>
          <tr>
            <td style={{ ...rc, border: 'none' }} />
            <td colSpan={4} style={{ ...rc, fontSize: '10pt', fontWeight: 600, paddingBottom: '6pt' }}>
              Inputs for pillow workorder
            </td>
          </tr>
        </thead>
        <tbody>
          {lineItems.map((item, idx) => {
            const p = parseNotes(item.notes);
            return (
              <React.Fragment key={item.id}>
                {idx > 0 && (
                  <tr><td colSpan={5} style={{ height: '10pt', border: 'none' }} /></tr>
                )}
                <tr>
                  <td style={{ ...rc, textAlign: 'right', fontWeight: 700, verticalAlign: 'middle' }}>{idx + 1}</td>
                  <td style={{ ...rc, border: 'none' }} />
                  <td style={{ ...rc, textAlign: 'right', textTransform: 'uppercase', fontWeight: 600, color: '#333' }}>Quantity</td>
                  <td style={{ ...rc }}>{Number(item.quantity)}</td>
                  <td style={{ ...rc, border: 'none' }} />
                </tr>
                {PILLOW_PRINT_FIELDS.map(([noteKey, label]) => (
                  <tr key={noteKey}>
                    <td style={{ ...rc, border: 'none' }} />
                    <td style={{ ...rc, border: 'none' }} />
                    <td style={{ ...rc, textAlign: 'right', textTransform: 'uppercase', fontWeight: 600, color: '#333' }}>{label}</td>
                    <td style={{ ...rc }}>{p[noteKey] || ''}</td>
                    <td style={{ ...rc, border: 'none' }} />
                  </tr>
                ))}
              </React.Fragment>
            );
          })}
          {wo.notes && (
            <tr>
              <td colSpan={5} style={{ ...rc, paddingTop: '10pt', color: '#444' }}>{wo.notes}</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function ShamPrintLayout({ wo, lineItems }: { wo: WorkOrderData; lineItems: LineItem[] }) {
  return (
    <div style={{ fontFamily: 'Arial, Helvetica, sans-serif', padding: '12pt 14pt', fontSize: '9pt' }}>
      <PrintHeader wo={wo} />
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <colgroup>
          <col style={{ width: '7%' }} />
          <col style={{ width: '16%' }} />
          <col style={{ width: '26%' }} />
          <col style={{ width: '36%' }} />
          <col style={{ width: '15%' }} />
        </colgroup>
        <thead>
          <tr>
            <td style={{ ...rc, border: 'none' }} />
            <td colSpan={4} style={{ ...rc, fontSize: '10pt', fontWeight: 600, paddingBottom: '6pt' }}>
              Inputs for sham workorder
            </td>
          </tr>
        </thead>
        <tbody>
          {lineItems.map((item, idx) => {
            const p = parseNotes(item.notes);
            return (
              <React.Fragment key={item.id}>
                {idx > 0 && (
                  <tr><td colSpan={5} style={{ height: '10pt', border: 'none' }} /></tr>
                )}
                <tr>
                  <td style={{ ...rc, textAlign: 'right', fontWeight: 700, verticalAlign: 'middle' }}>{idx + 1}</td>
                  <td style={{ ...rc, border: 'none' }} />
                  <td style={{ ...rc, textAlign: 'right', textTransform: 'uppercase', fontWeight: 600, color: '#333' }}>Quantity</td>
                  <td style={{ ...rc }}>{Number(item.quantity)}</td>
                  <td style={{ ...rc, border: 'none' }} />
                </tr>
                {SHAM_PRINT_FIELDS.map(([noteKey, label]) => (
                  <tr key={noteKey}>
                    <td style={{ ...rc, border: 'none' }} />
                    <td style={{ ...rc, border: 'none' }} />
                    <td style={{ ...rc, textAlign: 'right', textTransform: 'uppercase', fontWeight: 600, color: '#333' }}>{label}</td>
                    <td style={{ ...rc }}>{p[noteKey] || ''}</td>
                    <td style={{ ...rc, border: 'none' }} />
                  </tr>
                ))}
              </React.Fragment>
            );
          })}
          {wo.notes && (
            <tr>
              <td colSpan={5} style={{ ...rc, paddingTop: '10pt', color: '#444' }}>{wo.notes}</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function BolsterPrintLayout({ wo, lineItems }: { wo: WorkOrderData; lineItems: LineItem[] }) {
  return (
    <div style={{ fontFamily: 'Arial, Helvetica, sans-serif', padding: '12pt 14pt', fontSize: '9pt' }}>
      <PrintHeader wo={wo} />
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <colgroup>
          <col style={{ width: '7%' }} />
          <col style={{ width: '16%' }} />
          <col style={{ width: '26%' }} />
          <col style={{ width: '36%' }} />
          <col style={{ width: '15%' }} />
        </colgroup>
        <thead>
          <tr>
            <td style={{ ...rc, border: 'none' }} />
            <td colSpan={4} style={{ ...rc, fontSize: '10pt', fontWeight: 600, paddingBottom: '6pt' }}>
              Inputs for bolster pillow workorder
            </td>
          </tr>
        </thead>
        <tbody>
          {lineItems.map((item, idx) => {
            const p = parseNotes(item.notes);
            return (
              <React.Fragment key={item.id}>
                {idx > 0 && (
                  <tr><td colSpan={5} style={{ height: '10pt', border: 'none' }} /></tr>
                )}
                <tr>
                  <td style={{ ...rc, textAlign: 'right', fontWeight: 700, verticalAlign: 'middle' }}>{idx + 1}</td>
                  <td style={{ ...rc, border: 'none' }} />
                  <td style={{ ...rc, textAlign: 'right', textTransform: 'uppercase', fontWeight: 600, color: '#333' }}>Quantity</td>
                  <td style={{ ...rc }}>{Number(item.quantity)}</td>
                  <td style={{ ...rc, border: 'none' }} />
                </tr>
                {BOLSTER_PRINT_FIELDS.map(([noteKey, label]) => (
                  <tr key={noteKey}>
                    <td style={{ ...rc, border: 'none' }} />
                    <td style={{ ...rc, border: 'none' }} />
                    <td style={{ ...rc, textAlign: 'right', textTransform: 'uppercase', fontWeight: 600, color: '#333' }}>{label}</td>
                    <td style={{ ...rc }}>{p[noteKey] || ''}</td>
                    <td style={{ ...rc, border: 'none' }} />
                  </tr>
                ))}
              </React.Fragment>
            );
          })}
          {wo.notes && (
            <tr>
              <td colSpan={5} style={{ ...rc, paddingTop: '10pt', color: '#444' }}>{wo.notes}</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function CushionPrintLayout({ wo, lineItems }: { wo: WorkOrderData; lineItems: LineItem[] }) {
  return (
    <div style={{ fontFamily: 'Arial, Helvetica, sans-serif', padding: '12pt 14pt', fontSize: '9pt' }}>
      <PrintHeader wo={wo} />
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <colgroup>
          <col style={{ width: '7%' }} />
          <col style={{ width: '16%' }} />
          <col style={{ width: '26%' }} />
          <col style={{ width: '36%' }} />
          <col style={{ width: '15%' }} />
        </colgroup>
        <thead>
          <tr>
            <td style={{ ...rc, border: 'none' }} />
            <td colSpan={4} style={{ ...rc, fontSize: '10pt', fontWeight: 600, paddingBottom: '6pt' }}>
              Inputs for cushion workorder
            </td>
          </tr>
        </thead>
        <tbody>
          {lineItems.map((item, idx) => {
            const p = parseNotes(item.notes);
            return (
              <React.Fragment key={item.id}>
                {idx > 0 && (
                  <tr><td colSpan={5} style={{ height: '10pt', border: 'none' }} /></tr>
                )}
                <tr>
                  <td style={{ ...rc, textAlign: 'right', fontWeight: 700, verticalAlign: 'middle' }}>{idx + 1}</td>
                  <td style={{ ...rc, border: 'none' }} />
                  <td style={{ ...rc, textAlign: 'right', textTransform: 'uppercase', fontWeight: 600, color: '#333' }}>Quantity</td>
                  <td style={{ ...rc }}>{Number(item.quantity)}</td>
                  <td style={{ ...rc, border: 'none' }} />
                </tr>
                {CUSHION_PRINT_FIELDS.map(([noteKey, label]) => (
                  <tr key={noteKey}>
                    <td style={{ ...rc, border: 'none' }} />
                    <td style={{ ...rc, border: 'none' }} />
                    <td style={{ ...rc, textAlign: 'right', textTransform: 'uppercase', fontWeight: 600, color: '#333' }}>{label}</td>
                    <td style={{ ...rc }}>{p[noteKey] || ''}</td>
                    <td style={{ ...rc, border: 'none' }} />
                  </tr>
                ))}
              </React.Fragment>
            );
          })}
          {wo.notes && (
            <tr>
              <td colSpan={5} style={{ ...rc, paddingTop: '10pt', color: '#444' }}>{wo.notes}</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function DustskirtPrintLayout({ wo, lineItems }: { wo: WorkOrderData; lineItems: LineItem[] }) {
  return (
    <div style={{ fontFamily: 'Arial, Helvetica, sans-serif', padding: '12pt 14pt', fontSize: '9pt' }}>
      <PrintHeader wo={wo} />
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <colgroup>
          <col style={{ width: '7%' }} />
          <col style={{ width: '16%' }} />
          <col style={{ width: '26%' }} />
          <col style={{ width: '36%' }} />
          <col style={{ width: '15%' }} />
        </colgroup>
        <thead>
          <tr>
            <td style={{ ...rc, border: 'none' }} />
            <td colSpan={4} style={{ ...rc, fontSize: '10pt', fontWeight: 600, paddingBottom: '6pt' }}>
              Inputs for dustskirt workorder
            </td>
          </tr>
        </thead>
        <tbody>
          {lineItems.map((item, idx) => {
            const p = parseNotes(item.notes);
            return (
              <React.Fragment key={item.id}>
                {idx > 0 && (
                  <tr><td colSpan={5} style={{ height: '10pt', border: 'none' }} /></tr>
                )}
                <tr>
                  <td style={{ ...rc, textAlign: 'right', fontWeight: 700, verticalAlign: 'middle' }}>{idx + 1}</td>
                  <td style={{ ...rc, border: 'none' }} />
                  <td style={{ ...rc, textAlign: 'right', textTransform: 'uppercase', fontWeight: 600, color: '#333' }}>Quantity</td>
                  <td style={{ ...rc }}>{Number(item.quantity)}</td>
                  <td style={{ ...rc, border: 'none' }} />
                </tr>
                {DUSTSKIRT_PRINT_FIELDS.map(([noteKey, label]) => (
                  <tr key={noteKey}>
                    <td style={{ ...rc, border: 'none' }} />
                    <td style={{ ...rc, border: 'none' }} />
                    <td style={{ ...rc, textAlign: 'right', textTransform: 'uppercase', fontWeight: 600, color: '#333' }}>{label}</td>
                    <td style={{ ...rc }}>{p[noteKey] || ''}</td>
                    <td style={{ ...rc, border: 'none' }} />
                  </tr>
                ))}
              </React.Fragment>
            );
          })}
          {wo.notes && (
            <tr>
              <td colSpan={5} style={{ ...rc, paddingTop: '10pt', color: '#444' }}>{wo.notes}</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function BenchPrintLayout({ wo, lineItems }: { wo: WorkOrderData; lineItems: LineItem[] }) {
  return (
    <div style={{ fontFamily: 'Arial, Helvetica, sans-serif', padding: '12pt 14pt', fontSize: '9pt' }}>
      <PrintHeader wo={wo} />
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <colgroup>
          <col style={{ width: '7%' }} />
          <col style={{ width: '16%' }} />
          <col style={{ width: '26%' }} />
          <col style={{ width: '36%' }} />
          <col style={{ width: '15%' }} />
        </colgroup>
        <thead>
          <tr>
            <td style={{ ...rc, border: 'none' }} />
            <td colSpan={4} style={{ ...rc, fontSize: '10pt', fontWeight: 600, paddingBottom: '6pt' }}>
              Inputs for bench workorder
            </td>
          </tr>
        </thead>
        <tbody>
          {lineItems.map((item, idx) => {
            const p = parseNotes(item.notes);
            return (
              <React.Fragment key={item.id}>
                {idx > 0 && (
                  <tr><td colSpan={5} style={{ height: '10pt', border: 'none' }} /></tr>
                )}
                <tr>
                  <td style={{ ...rc, textAlign: 'right', fontWeight: 700, verticalAlign: 'middle' }}>{idx + 1}</td>
                  <td style={{ ...rc, border: 'none' }} />
                  <td style={{ ...rc, textAlign: 'right', textTransform: 'uppercase', fontWeight: 600, color: '#333' }}>Quantity</td>
                  <td style={{ ...rc }}>{Number(item.quantity)}</td>
                  <td style={{ ...rc, border: 'none' }} />
                </tr>
                {BENCH_PRINT_FIELDS.map(([noteKey, label]) => (
                  <tr key={noteKey}>
                    <td style={{ ...rc, border: 'none' }} />
                    <td style={{ ...rc, border: 'none' }} />
                    <td style={{ ...rc, textAlign: 'right', textTransform: 'uppercase', fontWeight: 600, color: '#333' }}>{label}</td>
                    <td style={{ ...rc }}>{p[noteKey] || ''}</td>
                    <td style={{ ...rc, border: 'none' }} />
                  </tr>
                ))}
              </React.Fragment>
            );
          })}
          {wo.notes && (
            <tr>
              <td colSpan={5} style={{ ...rc, paddingTop: '10pt', color: '#444' }}>{wo.notes}</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function BedPrintLayout({ wo, lineItems }: { wo: WorkOrderData; lineItems: LineItem[] }) {
  return (
    <div style={{ fontFamily: 'Arial, Helvetica, sans-serif', padding: '12pt 14pt', fontSize: '9pt' }}>
      <PrintHeader wo={wo} />
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <colgroup>
          <col style={{ width: '7%' }} />
          <col style={{ width: '16%' }} />
          <col style={{ width: '26%' }} />
          <col style={{ width: '36%' }} />
          <col style={{ width: '15%' }} />
        </colgroup>
        <thead>
          <tr>
            <td style={{ ...rc, border: 'none' }} />
            <td colSpan={4} style={{ ...rc, fontSize: '10pt', fontWeight: 600, paddingBottom: '6pt' }}>
              Inputs for bed workorder
            </td>
          </tr>
        </thead>
        <tbody>
          {lineItems.map((item, idx) => {
            const p = parseNotes(item.notes);
            return (
              <React.Fragment key={item.id}>
                {idx > 0 && (
                  <tr><td colSpan={5} style={{ height: '10pt', border: 'none' }} /></tr>
                )}
                <tr>
                  <td style={{ ...rc, textAlign: 'right', fontWeight: 700, verticalAlign: 'middle' }}>{idx + 1}</td>
                  <td style={{ ...rc, border: 'none' }} />
                  <td style={{ ...rc, textAlign: 'right', textTransform: 'uppercase', fontWeight: 600, color: '#333' }}>Quantity</td>
                  <td style={{ ...rc }}>{Number(item.quantity)}</td>
                  <td style={{ ...rc, border: 'none' }} />
                </tr>
                {BED_PRINT_FIELDS.map(([noteKey, label]) => (
                  <tr key={noteKey}>
                    <td style={{ ...rc, border: 'none' }} />
                    <td style={{ ...rc, border: 'none' }} />
                    <td style={{ ...rc, textAlign: 'right', textTransform: 'uppercase', fontWeight: 600, color: '#333' }}>{label}</td>
                    <td style={{ ...rc }}>{p[noteKey] || ''}</td>
                    <td style={{ ...rc, border: 'none' }} />
                  </tr>
                ))}
              </React.Fragment>
            );
          })}
          {wo.notes && (
            <tr>
              <td colSpan={5} style={{ ...rc, paddingTop: '10pt', color: '#444' }}>{wo.notes}</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function FurniturePrintLayout({ wo, lineItems }: { wo: WorkOrderData; lineItems: LineItem[] }) {
  return (
    <div style={{ fontFamily: 'Arial, Helvetica, sans-serif', padding: '12pt 14pt', fontSize: '9pt' }}>
      <PrintHeader wo={wo} />
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <colgroup>
          <col style={{ width: '7%' }} />
          <col style={{ width: '16%' }} />
          <col style={{ width: '26%' }} />
          <col style={{ width: '36%' }} />
          <col style={{ width: '15%' }} />
        </colgroup>
        <thead>
          <tr>
            <td style={{ ...rc, border: 'none' }} />
            <td colSpan={4} style={{ ...rc, fontSize: '10pt', fontWeight: 600, paddingBottom: '6pt' }}>
              Inputs for furniture workorder (no SOP yet — build spec only)
            </td>
          </tr>
        </thead>
        <tbody>
          {lineItems.map((item, idx) => {
            const p = parseNotes(item.notes);
            return (
              <React.Fragment key={item.id}>
                {idx > 0 && (
                  <tr><td colSpan={5} style={{ height: '10pt', border: 'none' }} /></tr>
                )}
                <tr>
                  <td style={{ ...rc, textAlign: 'right', fontWeight: 700, verticalAlign: 'middle' }}>{idx + 1}</td>
                  <td style={{ ...rc, border: 'none' }} />
                  <td style={{ ...rc, textAlign: 'right', textTransform: 'uppercase', fontWeight: 600, color: '#333' }}>Quantity</td>
                  <td style={{ ...rc }}>{Number(item.quantity)}</td>
                  <td style={{ ...rc, border: 'none' }} />
                </tr>
                {FURNITURE_PRINT_FIELDS.map(([noteKey, label]) => (
                  <tr key={noteKey}>
                    <td style={{ ...rc, border: 'none' }} />
                    <td style={{ ...rc, border: 'none' }} />
                    <td style={{ ...rc, textAlign: 'right', textTransform: 'uppercase', fontWeight: 600, color: '#333' }}>{label}</td>
                    <td style={{ ...rc }}>{p[noteKey] || ''}</td>
                    <td style={{ ...rc, border: 'none' }} />
                  </tr>
                ))}
              </React.Fragment>
            );
          })}
          {wo.notes && (
            <tr>
              <td colSpan={5} style={{ ...rc, paddingTop: '10pt', color: '#444' }}>{wo.notes}</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function AustrianShadePrintLayout({ wo, lineItems }: { wo: WorkOrderData; lineItems: LineItem[] }) {
  return (
    <div style={{ fontFamily: 'Arial, Helvetica, sans-serif', padding: '12pt 14pt', fontSize: '9pt' }}>
      <PrintHeader wo={wo} />
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <colgroup>
          <col style={{ width: '7%' }} />
          <col style={{ width: '16%' }} />
          <col style={{ width: '26%' }} />
          <col style={{ width: '36%' }} />
          <col style={{ width: '15%' }} />
        </colgroup>
        <thead>
          <tr>
            <td style={{ ...rc, border: 'none' }} />
            <td colSpan={4} style={{ ...rc, fontSize: '10pt', fontWeight: 600, paddingBottom: '6pt' }}>
              Inputs for austrian shade workorder
            </td>
          </tr>
        </thead>
        <tbody>
          {lineItems.map((item, idx) => {
            const p = parseNotes(item.notes);
            return (
              <React.Fragment key={item.id}>
                {idx > 0 && (
                  <tr><td colSpan={5} style={{ height: '10pt', border: 'none' }} /></tr>
                )}
                <tr>
                  <td style={{ ...rc, textAlign: 'right', fontWeight: 700, verticalAlign: 'middle' }}>{idx + 1}</td>
                  <td style={{ ...rc, border: 'none' }} />
                  <td style={{ ...rc, textAlign: 'right', textTransform: 'uppercase', fontWeight: 600, color: '#333' }}>Quantity</td>
                  <td style={{ ...rc }}>{Number(item.quantity)}</td>
                  <td style={{ ...rc, border: 'none' }} />
                </tr>
                {AUSTRIAN_SHADE_PRINT_FIELDS.map(([noteKey, label]) => (
                  <tr key={noteKey}>
                    <td style={{ ...rc, border: 'none' }} />
                    <td style={{ ...rc, border: 'none' }} />
                    <td style={{ ...rc, textAlign: 'right', textTransform: 'uppercase', fontWeight: 600, color: '#333' }}>{label}</td>
                    <td style={{ ...rc }}>{p[noteKey] || ''}</td>
                    <td style={{ ...rc, border: 'none' }} />
                  </tr>
                ))}
              </React.Fragment>
            );
          })}
          {wo.notes && (
            <tr>
              <td colSpan={5} style={{ ...rc, paddingTop: '10pt', color: '#444' }}>{wo.notes}</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function BoxSpringPrintLayout({ wo, lineItems }: { wo: WorkOrderData; lineItems: LineItem[] }) {
  return (
    <div style={{ fontFamily: 'Arial, Helvetica, sans-serif', padding: '12pt 14pt', fontSize: '9pt' }}>
      <PrintHeader wo={wo} />
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <colgroup>
          <col style={{ width: '7%' }} />
          <col style={{ width: '16%' }} />
          <col style={{ width: '26%' }} />
          <col style={{ width: '36%' }} />
          <col style={{ width: '15%' }} />
        </colgroup>
        <thead>
          <tr>
            <td style={{ ...rc, border: 'none' }} />
            <td colSpan={4} style={{ ...rc, fontSize: '10pt', fontWeight: 600, paddingBottom: '6pt' }}>
              Inputs for box spring cover workorder
            </td>
          </tr>
        </thead>
        <tbody>
          {lineItems.map((item, idx) => {
            const p = parseNotes(item.notes);
            return (
              <React.Fragment key={item.id}>
                {idx > 0 && (
                  <tr><td colSpan={5} style={{ height: '10pt', border: 'none' }} /></tr>
                )}
                <tr>
                  <td style={{ ...rc, textAlign: 'right', fontWeight: 700, verticalAlign: 'middle' }}>{idx + 1}</td>
                  <td style={{ ...rc, border: 'none' }} />
                  <td style={{ ...rc, textAlign: 'right', textTransform: 'uppercase', fontWeight: 600, color: '#333' }}>Quantity</td>
                  <td style={{ ...rc }}>{Number(item.quantity)}</td>
                  <td style={{ ...rc, border: 'none' }} />
                </tr>
                {BOX_SPRING_PRINT_FIELDS.map(([noteKey, label]) => (
                  <tr key={noteKey}>
                    <td style={{ ...rc, border: 'none' }} />
                    <td style={{ ...rc, border: 'none' }} />
                    <td style={{ ...rc, textAlign: 'right', textTransform: 'uppercase', fontWeight: 600, color: '#333' }}>{label}</td>
                    <td style={{ ...rc }}>{p[noteKey] || ''}</td>
                    <td style={{ ...rc, border: 'none' }} />
                  </tr>
                ))}
              </React.Fragment>
            );
          })}
          {wo.notes && (
            <tr>
              <td colSpan={5} style={{ ...rc, paddingTop: '10pt', color: '#444' }}>{wo.notes}</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function DuvetPrintLayout({ wo, lineItems }: { wo: WorkOrderData; lineItems: LineItem[] }) {
  return (
    <div style={{ fontFamily: 'Arial, Helvetica, sans-serif', padding: '12pt 14pt', fontSize: '9pt' }}>
      <PrintHeader wo={wo} />
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <colgroup>
          <col style={{ width: '7%' }} />
          <col style={{ width: '16%' }} />
          <col style={{ width: '26%' }} />
          <col style={{ width: '36%' }} />
          <col style={{ width: '15%' }} />
        </colgroup>
        <thead>
          <tr>
            <td style={{ ...rc, border: 'none' }} />
            <td colSpan={4} style={{ ...rc, fontSize: '10pt', fontWeight: 600, paddingBottom: '6pt' }}>
              Inputs for duvet workorder
            </td>
          </tr>
        </thead>
        <tbody>
          {lineItems.map((item, idx) => {
            const p = parseNotes(item.notes);
            return (
              <React.Fragment key={item.id}>
                {idx > 0 && (
                  <tr><td colSpan={5} style={{ height: '10pt', border: 'none' }} /></tr>
                )}
                <tr>
                  <td style={{ ...rc, textAlign: 'right', fontWeight: 700, verticalAlign: 'middle' }}>{idx + 1}</td>
                  <td style={{ ...rc, border: 'none' }} />
                  <td style={{ ...rc, textAlign: 'right', textTransform: 'uppercase', fontWeight: 600, color: '#333' }}>Quantity</td>
                  <td style={{ ...rc }}>{Number(item.quantity)}</td>
                  <td style={{ ...rc, border: 'none' }} />
                </tr>
                {DUVET_PRINT_FIELDS.map(([noteKey, label]) => (
                  <tr key={noteKey}>
                    <td style={{ ...rc, border: 'none' }} />
                    <td style={{ ...rc, border: 'none' }} />
                    <td style={{ ...rc, textAlign: 'right', textTransform: 'uppercase', fontWeight: 600, color: '#333' }}>{label}</td>
                    <td style={{ ...rc }}>{p[noteKey] || ''}</td>
                    <td style={{ ...rc, border: 'none' }} />
                  </tr>
                ))}
              </React.Fragment>
            );
          })}
          {wo.notes && (
            <tr>
              <td colSpan={5} style={{ ...rc, paddingTop: '10pt', color: '#444' }}>{wo.notes}</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function CoverletPrintLayout({ wo, lineItems }: { wo: WorkOrderData; lineItems: LineItem[] }) {
  return (
    <div style={{ fontFamily: 'Arial, Helvetica, sans-serif', padding: '12pt 14pt', fontSize: '9pt' }}>
      <PrintHeader wo={wo} />
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <colgroup>
          <col style={{ width: '7%' }} />
          <col style={{ width: '16%' }} />
          <col style={{ width: '26%' }} />
          <col style={{ width: '36%' }} />
          <col style={{ width: '15%' }} />
        </colgroup>
        <thead>
          <tr>
            <td style={{ ...rc, border: 'none' }} />
            <td colSpan={4} style={{ ...rc, fontSize: '10pt', fontWeight: 600, paddingBottom: '6pt' }}>
              Inputs for coverlet workorder
            </td>
          </tr>
        </thead>
        <tbody>
          {lineItems.map((item, idx) => {
            const p = parseNotes(item.notes);
            return (
              <React.Fragment key={item.id}>
                {idx > 0 && (
                  <tr><td colSpan={5} style={{ height: '10pt', border: 'none' }} /></tr>
                )}
                <tr>
                  <td style={{ ...rc, textAlign: 'right', fontWeight: 700, verticalAlign: 'middle' }}>{idx + 1}</td>
                  <td style={{ ...rc, border: 'none' }} />
                  <td style={{ ...rc, textAlign: 'right', textTransform: 'uppercase', fontWeight: 600, color: '#333' }}>Quantity</td>
                  <td style={{ ...rc }}>{Number(item.quantity)}</td>
                  <td style={{ ...rc, border: 'none' }} />
                </tr>
                {COVERLET_PRINT_FIELDS.map(([noteKey, label]) => (
                  <tr key={noteKey}>
                    <td style={{ ...rc, border: 'none' }} />
                    <td style={{ ...rc, border: 'none' }} />
                    <td style={{ ...rc, textAlign: 'right', textTransform: 'uppercase', fontWeight: 600, color: '#333' }}>{label}</td>
                    <td style={{ ...rc }}>{p[noteKey] || ''}</td>
                    <td style={{ ...rc, border: 'none' }} />
                  </tr>
                ))}
              </React.Fragment>
            );
          })}
          {wo.notes && (
            <tr>
              <td colSpan={5} style={{ ...rc, paddingTop: '10pt', color: '#444' }}>{wo.notes}</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function TableclothRoundPrintLayout({ wo, lineItems }: { wo: WorkOrderData; lineItems: LineItem[] }) {
  return (
    <div style={{ fontFamily: 'Arial, Helvetica, sans-serif', padding: '12pt 14pt', fontSize: '9pt' }}>
      <PrintHeader wo={wo} />
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <colgroup>
          <col style={{ width: '7%' }} />
          <col style={{ width: '16%' }} />
          <col style={{ width: '26%' }} />
          <col style={{ width: '36%' }} />
          <col style={{ width: '15%' }} />
        </colgroup>
        <thead>
          <tr>
            <td style={{ ...rc, border: 'none' }} />
            <td colSpan={4} style={{ ...rc, fontSize: '10pt', fontWeight: 600, paddingBottom: '6pt' }}>
              Inputs for round tablecloth workorder
            </td>
          </tr>
        </thead>
        <tbody>
          {lineItems.map((item, idx) => {
            const p = parseNotes(item.notes);
            return (
              <React.Fragment key={item.id}>
                {idx > 0 && (
                  <tr><td colSpan={5} style={{ height: '10pt', border: 'none' }} /></tr>
                )}
                <tr>
                  <td style={{ ...rc, textAlign: 'right', fontWeight: 700, verticalAlign: 'middle' }}>{idx + 1}</td>
                  <td style={{ ...rc, border: 'none' }} />
                  <td style={{ ...rc, textAlign: 'right', textTransform: 'uppercase', fontWeight: 600, color: '#333' }}>Quantity</td>
                  <td style={{ ...rc }}>{Number(item.quantity)}</td>
                  <td style={{ ...rc, border: 'none' }} />
                </tr>
                {TABLECLOTH_ROUND_PRINT_FIELDS.map(([noteKey, label]) => (
                  <tr key={noteKey}>
                    <td style={{ ...rc, border: 'none' }} />
                    <td style={{ ...rc, border: 'none' }} />
                    <td style={{ ...rc, textAlign: 'right', textTransform: 'uppercase', fontWeight: 600, color: '#333' }}>{label}</td>
                    <td style={{ ...rc }}>{p[noteKey] || ''}</td>
                    <td style={{ ...rc, border: 'none' }} />
                  </tr>
                ))}
              </React.Fragment>
            );
          })}
          {wo.notes && (
            <tr>
              <td colSpan={5} style={{ ...rc, paddingTop: '10pt', color: '#444' }}>{wo.notes}</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function TableclothRectanglePrintLayout({ wo, lineItems }: { wo: WorkOrderData; lineItems: LineItem[] }) {
  return (
    <div style={{ fontFamily: 'Arial, Helvetica, sans-serif', padding: '12pt 14pt', fontSize: '9pt' }}>
      <PrintHeader wo={wo} />
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <colgroup>
          <col style={{ width: '7%' }} />
          <col style={{ width: '16%' }} />
          <col style={{ width: '26%' }} />
          <col style={{ width: '36%' }} />
          <col style={{ width: '15%' }} />
        </colgroup>
        <thead>
          <tr>
            <td style={{ ...rc, border: 'none' }} />
            <td colSpan={4} style={{ ...rc, fontSize: '10pt', fontWeight: 600, paddingBottom: '6pt' }}>
              Inputs for rectangle tablecloth workorder
            </td>
          </tr>
        </thead>
        <tbody>
          {lineItems.map((item, idx) => {
            const p = parseNotes(item.notes);
            return (
              <React.Fragment key={item.id}>
                {idx > 0 && (
                  <tr><td colSpan={5} style={{ height: '10pt', border: 'none' }} /></tr>
                )}
                <tr>
                  <td style={{ ...rc, textAlign: 'right', fontWeight: 700, verticalAlign: 'middle' }}>{idx + 1}</td>
                  <td style={{ ...rc, border: 'none' }} />
                  <td style={{ ...rc, textAlign: 'right', textTransform: 'uppercase', fontWeight: 600, color: '#333' }}>Quantity</td>
                  <td style={{ ...rc }}>{Number(item.quantity)}</td>
                  <td style={{ ...rc, border: 'none' }} />
                </tr>
                {TABLECLOTH_RECTANGLE_PRINT_FIELDS.map(([noteKey, label]) => (
                  <tr key={noteKey}>
                    <td style={{ ...rc, border: 'none' }} />
                    <td style={{ ...rc, border: 'none' }} />
                    <td style={{ ...rc, textAlign: 'right', textTransform: 'uppercase', fontWeight: 600, color: '#333' }}>{label}</td>
                    <td style={{ ...rc }}>{p[noteKey] || ''}</td>
                    <td style={{ ...rc, border: 'none' }} />
                  </tr>
                ))}
              </React.Fragment>
            );
          })}
          {wo.notes && (
            <tr>
              <td colSpan={5} style={{ ...rc, paddingTop: '10pt', color: '#444' }}>{wo.notes}</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// Print target for the Photos section's "Print Photos" button — a plain
// two-up photo grid, not a traveler layout, since these are reference
// blueprint/quote images rather than build-spec data.
function PhotosPrintLayout({ wo, photos }: { wo: WorkOrderData; photos: GalleryPhoto[] }) {
  return (
    <div style={{ fontFamily: 'Arial, Helvetica, sans-serif', padding: '12pt 14pt', fontSize: '9pt' }}>
      <PrintHeader wo={wo} />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10pt' }}>
        {photos.map((p) => (
          <div key={p.key} style={{ breakInside: 'avoid', textAlign: 'center' }}>
            <img
              src={p.url}
              alt={p.filename}
              style={{ width: '100%', maxHeight: '260pt', objectFit: 'contain', border: '1px solid #d1d5db' }}
            />
            <div style={{ marginTop: '4pt', fontSize: '8pt', color: '#555' }}>{p.filename}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function WorkOrderPrintLayout({ wo, lineItems }: { wo: WorkOrderData; lineItems: LineItem[] }) {
  if (wo.type === 'roller_shade') {
    return <RollerShadePrintLayout wo={wo} lineItems={lineItems} />;
  }
  if (wo.type === 'pillow') {
    return <PillowPrintLayout wo={wo} lineItems={lineItems} />;
  }
  if (wo.type === 'sham') {
    return <ShamPrintLayout wo={wo} lineItems={lineItems} />;
  }
  if (wo.type === 'bolster_pillow') {
    return <BolsterPrintLayout wo={wo} lineItems={lineItems} />;
  }
  if (wo.type === 'cushion') {
    return <CushionPrintLayout wo={wo} lineItems={lineItems} />;
  }
  if (wo.type === 'dustskirt') {
    return <DustskirtPrintLayout wo={wo} lineItems={lineItems} />;
  }
  if (wo.type === 'austrian_shade') {
    return <AustrianShadePrintLayout wo={wo} lineItems={lineItems} />;
  }
  if (wo.type === 'balloon_shade') {
    return <BalloonShadePrintLayout wo={wo} lineItems={lineItems} />;
  }
  if (wo.type === 'roman_shade') {
    return <RomanShadePrintLayout wo={wo} lineItems={lineItems} />;
  }
  if (wo.type === 'roman_valance') {
    return <RomanValancePrintLayout wo={wo} lineItems={lineItems} />;
  }
  if (wo.type === 'pleated_valance' || wo.type === 'tailored_valance' || wo.type === 'shirred_valance') {
    return <PleatedValancePrintLayout wo={wo} lineItems={lineItems} />;
  }
  if (wo.type === 'box_spring_cover') {
    return <BoxSpringPrintLayout wo={wo} lineItems={lineItems} />;
  }
  if (wo.type === 'duvet') {
    return <DuvetPrintLayout wo={wo} lineItems={lineItems} />;
  }
  if (wo.type === 'coverlet') {
    return <CoverletPrintLayout wo={wo} lineItems={lineItems} />;
  }
  if (wo.type === 'tablecloth_round') {
    return <TableclothRoundPrintLayout wo={wo} lineItems={lineItems} />;
  }
  if (wo.type === 'tablecloth_rectangle') {
    return <TableclothRectanglePrintLayout wo={wo} lineItems={lineItems} />;
  }
  if (wo.type === 'bench') {
    return <BenchPrintLayout wo={wo} lineItems={lineItems} />;
  }
  if (wo.type === 'bed') {
    return <BedPrintLayout wo={wo} lineItems={lineItems} />;
  }
  if (wo.type === 'furniture') {
    return <FurniturePrintLayout wo={wo} lineItems={lineItems} />;
  }
  // Traversing Drape/Side Panel share this WorkOrderType ('drapery') and
  // both fall through to this fallback branch — per issue #59, printed
  // drapery work orders get a light pink background (reusing the existing
  // LIGHT_PINK brand accent from QuoteDetail.tsx). print-color-adjust is
  // required alongside backgroundColor — browsers strip printed background
  // colors by default unless this is set (and/or "Background graphics" is
  // enabled in the browser's print dialog).
  const drapeyPrintStyle: React.CSSProperties = wo.type === 'drapery'
    ? { backgroundColor: '#f5d0e8', WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact' }
    : {};
  return (
    <div style={{ fontFamily: 'Arial, Helvetica, sans-serif', padding: '12pt 14pt', fontSize: '9pt', ...drapeyPrintStyle }}>
      <PrintHeader wo={wo} />

      {/* ── Line items ── */}
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            {TRAVELER_COLS.map((col, i) => (
              <th key={i} style={hCell}>
                {col.top && <div>{col.top}</div>}
                <div>{col.bot}</div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {lineItems.map((item) => {
            const p = parseNotes(item.notes);
            return (
              <tr key={item.id}>
                <td style={{ ...cell, textAlign: 'left' }}>{item.description}</td>
                <td style={cell}>{Number(item.quantity) % 1 === 0 ? Number(item.quantity) : Number(item.quantity).toFixed(2)}</td>
                {TRAVELER_COLS.slice(2).map((col, i) => (
                  <td key={i} style={cell}>{col.key ? (p[col.key] || '') : ''}</td>
                ))}
              </tr>
            );
          })}
          {/* Notes row */}
          {wo.notes && (
            <tr>
              <td style={{ ...cell, height: '36pt' }} />
              <td style={{ ...cell, height: '36pt' }} />
              <td colSpan={TRAVELER_COLS.length - 2} style={{ ...cell, textAlign: 'left', verticalAlign: 'top', padding: '5pt 6pt', lineHeight: 1.5 }}>
                {wo.notes}
              </td>
            </tr>
          )}
          {/* Blank row for handwritten notes */}
          <tr>
            <td style={{ ...cell, height: '40pt' }} />
            <td style={{ ...cell, height: '40pt' }} />
            <td colSpan={TRAVELER_COLS.length - 2} style={{ ...cell, height: '40pt' }} />
          </tr>
        </tbody>
      </table>
    </div>
  );
}

// ── Traveler items (drapery + roman shade) ────────────────────────────────────

const TRAVELER_DISPLAY_COLS: { label: string; key: string | null }[] = [
  { label: 'Fabric/Color', key: null },
  { label: 'Yds', key: null },
  { label: 'Qty', key: 'Qty' },
  { label: '# Prs', key: 'Prs' },
  { label: 'Width/Panel', key: 'Width/Panel' },
  { label: 'Rod Face', key: 'Rod Face' },
  { label: 'F.W.', key: 'F.W.' },
  { label: 'F.L.', key: 'F.L.' },
  { label: 'Hem Size', key: 'Hem' },
  { label: 'Returns', key: 'Returns' },
  { label: 'Overlaps', key: 'Overlaps' },
  { label: 'Line', key: 'Line' },
  { label: 'Inter-Line', key: 'Inter-Line' },
  { label: 'Room/Window', key: 'Room/Window' },
  { label: 'Hardware', key: 'Hardware' },
  { label: 'Pleat Type', key: 'Pleat Type' },
  { label: 'Status', key: null },
];

function TravelerItems({ items, liChanging, onAdvance }: { items: LineItem[]; liChanging: number | null; onAdvance: (item: LineItem) => void }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ borderCollapse: 'collapse', fontSize: 13, width: '100%', minWidth: 900 }}>
        <thead>
          <tr>
            {TRAVELER_DISPLAY_COLS.map((col) => (
              <th key={col.label} style={s.th}>{col.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map((item) => {
            const parsed = parseNotes(item.notes);
            const liColors = liStatusColors[item.status] || liStatusColors.pending;
            return (
              <tr key={item.id} style={{ backgroundColor: item.status === 'completed' ? '#f0fdf4' : undefined }}>
                <td style={s.td}>{item.description}</td>
                <td style={{ ...s.td, textAlign: 'center' }}>{Number(item.quantity)}</td>
                {(['Qty', 'Prs', 'Width/Panel', 'Rod Face', 'F.W.', 'F.L.', 'Hem', 'Returns', 'Overlaps', 'Line', 'Inter-Line', 'Room/Window', 'Hardware', 'Pleat Type'] as const).map((key) => (
                  <td key={key} style={{ ...s.td, textAlign: 'center' }}>{parsed[key] || '—'}</td>
                ))}
                <td className="no-print" style={{ ...s.td, textAlign: 'center' }}>
                  <button
                    onClick={() => onAdvance(item)}
                    disabled={liChanging === item.id}
                    style={{ padding: '3px 10px', border: 'none', borderRadius: 12, backgroundColor: liColors.bg, color: liColors.text, fontWeight: 600, fontSize: 11, cursor: 'pointer', whiteSpace: 'nowrap', textTransform: 'capitalize' }}
                    title="Click to advance status"
                  >
                    {item.status.replace('_', ' ')}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── Roller shade items ────────────────────────────────────────────────────────

const ROLLER_DISPLAY_FIELDS: [string, string][] = [
  ['Window Width',   'WINDOW WIDTH'],
  ['Window Length',  'WINDOW LENGTH'],
  ['Tube Size',      'TUBE SIZE'],
  ['Fabric',         'FABRIC'],
  ['Drive',          'DRIVE'],
  ['Operating System', 'OPERATING SYSTEM'],
  ['Roll Direction', 'ROLL DIRECTION'],
  ['RT or LT',       'RT or LT'],
  ['Inside/Outside', 'Inside/Outside'],
];

function RollerShadeItems({ items, liChanging, onAdvance }: { items: LineItem[]; liChanging: number | null; onAdvance: (item: LineItem) => void }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
      {items.map((item, idx) => {
        const parsed = parseNotes(item.notes);
        const liColors = liStatusColors[item.status] || liStatusColors.pending;
        return (
          <div
            key={item.id}
            style={{ border: '1px solid #d1d5db', borderRadius: 6, backgroundColor: item.status === 'completed' ? '#f0fdf4' : '#fff', padding: '14px 16px', minWidth: 280, flex: '1 1 280px', maxWidth: 340 }}
          >
            {/* Shade number + status */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <span style={{ fontWeight: 700, fontSize: 18, color: '#374151' }}>{idx + 1}</span>
              <button
                onClick={() => onAdvance(item)}
                disabled={liChanging === item.id}
                style={{ padding: '3px 10px', border: 'none', borderRadius: 12, backgroundColor: liColors.bg, color: liColors.text, fontWeight: 600, fontSize: 11, cursor: 'pointer', textTransform: 'capitalize' }}
                title="Click to advance status"
              >
                {item.status.replace('_', ' ')}
              </button>
            </div>

            {/* Fields */}
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                {/* Room/Window is the description */}
                <tr>
                  <td style={s.rollerLabel}>ROOM/WINDOW</td>
                  <td style={s.rollerValue}>{item.description}</td>
                </tr>
                {ROLLER_DISPLAY_FIELDS.map(([parseKey, displayLabel]) => (
                  <tr key={parseKey}>
                    <td style={s.rollerLabel}>{displayLabel}</td>
                    <td style={s.rollerValue}>{parsed[parseKey] || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
}

// ── Pillow items ───────────────────────────────────────────────────────────────

const PILLOW_DISPLAY_FIELDS: [string, string][] = [
  ['Size',   'SIZE'],
  ['Zipper', 'ZIPPER'],
  ['Insert', 'INSERT'],
  ['Fabric', 'FABRIC'],
  ['Welt/Trim/Ruffle', 'WELT/TRIM/RUFFLE'],
  ['Contrast Welt Fabric', 'CONTRAST WELT FABRIC'],
  ['Trim Name', 'TRIM NAME'],
];

function PillowItems({ items, liChanging, onAdvance }: { items: LineItem[]; liChanging: number | null; onAdvance: (item: LineItem) => void }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
      {items.map((item, idx) => {
        const parsed = parseNotes(item.notes);
        const liColors = liStatusColors[item.status] || liStatusColors.pending;
        return (
          <div
            key={item.id}
            style={{ border: '1px solid #d1d5db', borderRadius: 6, backgroundColor: item.status === 'completed' ? '#f0fdf4' : '#fff', padding: '14px 16px', minWidth: 280, flex: '1 1 280px', maxWidth: 340 }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <span style={{ fontWeight: 700, fontSize: 18, color: '#374151' }}>{idx + 1}</span>
              <button
                onClick={() => onAdvance(item)}
                disabled={liChanging === item.id}
                style={{ padding: '3px 10px', border: 'none', borderRadius: 12, backgroundColor: liColors.bg, color: liColors.text, fontWeight: 600, fontSize: 11, cursor: 'pointer', textTransform: 'capitalize' }}
                title="Click to advance status"
              >
                {item.status.replace('_', ' ')}
              </button>
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                <tr>
                  <td style={s.rollerLabel}>ROOM/WINDOW</td>
                  <td style={s.rollerValue}>{item.description}</td>
                </tr>
                <tr>
                  <td style={s.rollerLabel}>QUANTITY</td>
                  <td style={s.rollerValue}>{Number(item.quantity)}</td>
                </tr>
                {PILLOW_DISPLAY_FIELDS.map(([parseKey, displayLabel]) => (
                  <tr key={parseKey}>
                    <td style={s.rollerLabel}>{displayLabel}</td>
                    <td style={s.rollerValue}>{parsed[parseKey] || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
}

// ── Sham items ─────────────────────────────────────────────────────────────────

const SHAM_DISPLAY_FIELDS: [string, string][] = [
  ['Size',   'SIZE'],
  ['Zipper', 'ZIPPER'],
  ['Insert', 'INSERT'],
  ['Fabric', 'FABRIC'],
  ['Welt/Trim/Ruffle', 'WELT/TRIM/RUFFLE'],
  ['Contrast Welt Fabric', 'CONTRAST WELT FABRIC'],
  ['Trim Name', 'TRIM NAME'],
];

function ShamItems({ items, liChanging, onAdvance }: { items: LineItem[]; liChanging: number | null; onAdvance: (item: LineItem) => void }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
      {items.map((item, idx) => {
        const parsed = parseNotes(item.notes);
        const liColors = liStatusColors[item.status] || liStatusColors.pending;
        return (
          <div
            key={item.id}
            style={{ border: '1px solid #d1d5db', borderRadius: 6, backgroundColor: item.status === 'completed' ? '#f0fdf4' : '#fff', padding: '14px 16px', minWidth: 280, flex: '1 1 280px', maxWidth: 340 }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <span style={{ fontWeight: 700, fontSize: 18, color: '#374151' }}>{idx + 1}</span>
              <button
                onClick={() => onAdvance(item)}
                disabled={liChanging === item.id}
                style={{ padding: '3px 10px', border: 'none', borderRadius: 12, backgroundColor: liColors.bg, color: liColors.text, fontWeight: 600, fontSize: 11, cursor: 'pointer', textTransform: 'capitalize' }}
                title="Click to advance status"
              >
                {item.status.replace('_', ' ')}
              </button>
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                <tr>
                  <td style={s.rollerLabel}>ROOM/WINDOW</td>
                  <td style={s.rollerValue}>{item.description}</td>
                </tr>
                <tr>
                  <td style={s.rollerLabel}>QUANTITY</td>
                  <td style={s.rollerValue}>{Number(item.quantity)}</td>
                </tr>
                {SHAM_DISPLAY_FIELDS.map(([parseKey, displayLabel]) => (
                  <tr key={parseKey}>
                    <td style={s.rollerLabel}>{displayLabel}</td>
                    <td style={s.rollerValue}>{parsed[parseKey] || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
}

// ── Bolster Pillow items ─────────────────────────────────────────────────────

const BOLSTER_DISPLAY_FIELDS: [string, string][] = [
  ['Size',   'SIZE'],
  ['Zipper', 'ZIPPER'],
  ['Insert', 'INSERT'],
  ['Fabric', 'FABRIC'],
  ['Welt/Trim/Ruffle', 'WELT/TRIM/RUFFLE'],
  ['Contrast Welt Fabric', 'CONTRAST WELT FABRIC'],
  ['Trim Name', 'TRIM NAME'],
];

function BolsterItems({ items, liChanging, onAdvance }: { items: LineItem[]; liChanging: number | null; onAdvance: (item: LineItem) => void }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
      {items.map((item, idx) => {
        const parsed = parseNotes(item.notes);
        const liColors = liStatusColors[item.status] || liStatusColors.pending;
        return (
          <div
            key={item.id}
            style={{ border: '1px solid #d1d5db', borderRadius: 6, backgroundColor: item.status === 'completed' ? '#f0fdf4' : '#fff', padding: '14px 16px', minWidth: 280, flex: '1 1 280px', maxWidth: 340 }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <span style={{ fontWeight: 700, fontSize: 18, color: '#374151' }}>{idx + 1}</span>
              <button
                onClick={() => onAdvance(item)}
                disabled={liChanging === item.id}
                style={{ padding: '3px 10px', border: 'none', borderRadius: 12, backgroundColor: liColors.bg, color: liColors.text, fontWeight: 600, fontSize: 11, cursor: 'pointer', textTransform: 'capitalize' }}
                title="Click to advance status"
              >
                {item.status.replace('_', ' ')}
              </button>
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                <tr>
                  <td style={s.rollerLabel}>ROOM/WINDOW</td>
                  <td style={s.rollerValue}>{item.description}</td>
                </tr>
                <tr>
                  <td style={s.rollerLabel}>QUANTITY</td>
                  <td style={s.rollerValue}>{Number(item.quantity)}</td>
                </tr>
                {BOLSTER_DISPLAY_FIELDS.map(([parseKey, displayLabel]) => (
                  <tr key={parseKey}>
                    <td style={s.rollerLabel}>{displayLabel}</td>
                    <td style={s.rollerValue}>{parsed[parseKey] || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
}

const CUSHION_DISPLAY_FIELDS: [string, string][] = [
  ['Size',   'SIZE'],
  ['Zipper', 'ZIPPER'],
  ['Insert', 'INSERT'],
  ['Fabric', 'FABRIC'],
  ['Welt/Trim', 'WELT/TRIM'],
];

function CushionItems({ items, liChanging, onAdvance }: { items: LineItem[]; liChanging: number | null; onAdvance: (item: LineItem) => void }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
      {items.map((item, idx) => {
        const parsed = parseNotes(item.notes);
        const liColors = liStatusColors[item.status] || liStatusColors.pending;
        return (
          <div
            key={item.id}
            style={{ border: '1px solid #d1d5db', borderRadius: 6, backgroundColor: item.status === 'completed' ? '#f0fdf4' : '#fff', padding: '14px 16px', minWidth: 280, flex: '1 1 280px', maxWidth: 340 }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <span style={{ fontWeight: 700, fontSize: 18, color: '#374151' }}>{idx + 1}</span>
              <button
                onClick={() => onAdvance(item)}
                disabled={liChanging === item.id}
                style={{ padding: '3px 10px', border: 'none', borderRadius: 12, backgroundColor: liColors.bg, color: liColors.text, fontWeight: 600, fontSize: 11, cursor: 'pointer', textTransform: 'capitalize' }}
                title="Click to advance status"
              >
                {item.status.replace('_', ' ')}
              </button>
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                <tr>
                  <td style={s.rollerLabel}>ROOM/WINDOW</td>
                  <td style={s.rollerValue}>{item.description}</td>
                </tr>
                <tr>
                  <td style={s.rollerLabel}>QUANTITY</td>
                  <td style={s.rollerValue}>{Number(item.quantity)}</td>
                </tr>
                {CUSHION_DISPLAY_FIELDS.map(([parseKey, displayLabel]) => (
                  <tr key={parseKey}>
                    <td style={s.rollerLabel}>{displayLabel}</td>
                    <td style={s.rollerValue}>{parsed[parseKey] || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
}

const DUSTSKIRT_DISPLAY_FIELDS: [string, string][] = [
  ['Bed Size', 'BED SIZE'],
  ['Style',    'STYLE'],
  ['Fabric',   'FABRIC'],
  ['Drop',     'DROP'],
];

function DustskirtItems({ items, liChanging, onAdvance }: { items: LineItem[]; liChanging: number | null; onAdvance: (item: LineItem) => void }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
      {items.map((item, idx) => {
        const parsed = parseNotes(item.notes);
        const liColors = liStatusColors[item.status] || liStatusColors.pending;
        return (
          <div
            key={item.id}
            style={{ border: '1px solid #d1d5db', borderRadius: 6, backgroundColor: item.status === 'completed' ? '#f0fdf4' : '#fff', padding: '14px 16px', minWidth: 280, flex: '1 1 280px', maxWidth: 340 }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <span style={{ fontWeight: 700, fontSize: 18, color: '#374151' }}>{idx + 1}</span>
              <button
                onClick={() => onAdvance(item)}
                disabled={liChanging === item.id}
                style={{ padding: '3px 10px', border: 'none', borderRadius: 12, backgroundColor: liColors.bg, color: liColors.text, fontWeight: 600, fontSize: 11, cursor: 'pointer', textTransform: 'capitalize' }}
                title="Click to advance status"
              >
                {item.status.replace('_', ' ')}
              </button>
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                <tr>
                  <td style={s.rollerLabel}>ROOM/WINDOW</td>
                  <td style={s.rollerValue}>{item.description}</td>
                </tr>
                <tr>
                  <td style={s.rollerLabel}>QUANTITY</td>
                  <td style={s.rollerValue}>{Number(item.quantity)}</td>
                </tr>
                {DUSTSKIRT_DISPLAY_FIELDS.map(([parseKey, displayLabel]) => (
                  <tr key={parseKey}>
                    <td style={s.rollerLabel}>{displayLabel}</td>
                    <td style={s.rollerValue}>{parsed[parseKey] || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
}

function BenchItems({ items, liChanging, onAdvance }: { items: LineItem[]; liChanging: number | null; onAdvance: (item: LineItem) => void }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
      {items.map((item, idx) => {
        const parsed = parseNotes(item.notes);
        const liColors = liStatusColors[item.status] || liStatusColors.pending;
        return (
          <div
            key={item.id}
            style={{ border: '1px solid #d1d5db', borderRadius: 6, backgroundColor: item.status === 'completed' ? '#f0fdf4' : '#fff', padding: '14px 16px', minWidth: 280, flex: '1 1 280px', maxWidth: 340 }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <span style={{ fontWeight: 700, fontSize: 18, color: '#374151' }}>{idx + 1}</span>
              <button
                onClick={() => onAdvance(item)}
                disabled={liChanging === item.id}
                style={{ padding: '3px 10px', border: 'none', borderRadius: 12, backgroundColor: liColors.bg, color: liColors.text, fontWeight: 600, fontSize: 11, cursor: 'pointer', textTransform: 'capitalize' }}
                title="Click to advance status"
              >
                {item.status.replace('_', ' ')}
              </button>
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                <tr>
                  <td style={s.rollerLabel}>ROOM/PIECE</td>
                  <td style={s.rollerValue}>{item.description}</td>
                </tr>
                <tr>
                  <td style={s.rollerLabel}>QUANTITY</td>
                  <td style={s.rollerValue}>{Number(item.quantity)}</td>
                </tr>
                {BENCH_PRINT_FIELDS.map(([parseKey, displayLabel]) => (
                  <tr key={parseKey}>
                    <td style={s.rollerLabel}>{displayLabel}</td>
                    <td style={s.rollerValue}>{parsed[parseKey] || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
}

function BedItems({ items, liChanging, onAdvance }: { items: LineItem[]; liChanging: number | null; onAdvance: (item: LineItem) => void }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
      {items.map((item, idx) => {
        const parsed = parseNotes(item.notes);
        const liColors = liStatusColors[item.status] || liStatusColors.pending;
        return (
          <div
            key={item.id}
            style={{ border: '1px solid #d1d5db', borderRadius: 6, backgroundColor: item.status === 'completed' ? '#f0fdf4' : '#fff', padding: '14px 16px', minWidth: 280, flex: '1 1 280px', maxWidth: 340 }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <span style={{ fontWeight: 700, fontSize: 18, color: '#374151' }}>{idx + 1}</span>
              <button
                onClick={() => onAdvance(item)}
                disabled={liChanging === item.id}
                style={{ padding: '3px 10px', border: 'none', borderRadius: 12, backgroundColor: liColors.bg, color: liColors.text, fontWeight: 600, fontSize: 11, cursor: 'pointer', textTransform: 'capitalize' }}
                title="Click to advance status"
              >
                {item.status.replace('_', ' ')}
              </button>
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                <tr>
                  <td style={s.rollerLabel}>ROOM/PIECE</td>
                  <td style={s.rollerValue}>{item.description}</td>
                </tr>
                <tr>
                  <td style={s.rollerLabel}>QUANTITY</td>
                  <td style={s.rollerValue}>{Number(item.quantity)}</td>
                </tr>
                {BED_PRINT_FIELDS.map(([parseKey, displayLabel]) => (
                  <tr key={parseKey}>
                    <td style={s.rollerLabel}>{displayLabel}</td>
                    <td style={s.rollerValue}>{parsed[parseKey] || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
}

function FurnitureItems({ items, liChanging, onAdvance, isWorker }: { items: LineItem[]; liChanging: number | null; onAdvance: (item: LineItem) => void; isWorker?: boolean }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
      {items.map((item, idx) => {
        const parsed = parseNotes(item.notes);
        const liColors = liStatusColors[item.status] || liStatusColors.pending;
        return (
          <div
            key={item.id}
            style={{ border: '1px solid #d1d5db', borderRadius: 6, backgroundColor: item.status === 'completed' ? '#f0fdf4' : '#fff', padding: '14px 16px', minWidth: 280, flex: '1 1 280px', maxWidth: 340 }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <span style={{ fontWeight: 700, fontSize: 18, color: '#374151' }}>{idx + 1}</span>
              <button
                onClick={() => onAdvance(item)}
                disabled={liChanging === item.id}
                style={{ padding: '3px 10px', border: 'none', borderRadius: 12, backgroundColor: liColors.bg, color: liColors.text, fontWeight: 600, fontSize: 11, cursor: 'pointer', textTransform: 'capitalize' }}
                title="Click to advance status"
              >
                {item.status.replace('_', ' ')}
              </button>
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                <tr>
                  <td style={s.rollerLabel}>ROOM/PIECE</td>
                  <td style={s.rollerValue}>{item.description}</td>
                </tr>
                <tr>
                  <td style={s.rollerLabel}>QUANTITY</td>
                  <td style={s.rollerValue}>{Number(item.quantity)}</td>
                </tr>
                {FURNITURE_PRINT_FIELDS.map(([parseKey, displayLabel]) => (
                  <tr key={parseKey}>
                    <td style={s.rollerLabel}>{displayLabel}</td>
                    <td style={s.rollerValue}>{parsed[parseKey] || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {item.draftQuoteObjectId != null && !isWorker && (
              <Link
                to={`/blueprints?draftQuoteObjectId=${item.draftQuoteObjectId}`}
                style={{ display: 'inline-block', marginTop: 12, fontSize: 12, fontWeight: 600, color: '#4f46e5', textDecoration: 'none' }}
              >
                Design in Blueprints →
              </Link>
            )}
          </div>
        );
      })}
    </div>
  );
}

const AUSTRIAN_SHADE_DISPLAY_FIELDS: [string, string][] = [
  ['Width',       'WIDTH'],
  ['Length',      'LENGTH'],
  ['Mount',       'MOUNT'],
  ['Fabric',      'FABRIC'],
  ['Lining',      'LINING'],
  ['Interlining', 'INTERLINING'],
  ['Trim',        'TRIM'],
  ['Operating System', 'OPERATING SYSTEM'],
  ['Board',       'BOARD'],
  ['Returns',     'RETURNS'],
];

function AustrianShadeItems({ items, liChanging, onAdvance }: { items: LineItem[]; liChanging: number | null; onAdvance: (item: LineItem) => void }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
      {items.map((item, idx) => {
        const parsed = parseNotes(item.notes);
        const liColors = liStatusColors[item.status] || liStatusColors.pending;
        return (
          <div
            key={item.id}
            style={{ border: '1px solid #d1d5db', borderRadius: 6, backgroundColor: item.status === 'completed' ? '#f0fdf4' : '#fff', padding: '14px 16px', minWidth: 280, flex: '1 1 280px', maxWidth: 340 }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <span style={{ fontWeight: 700, fontSize: 18, color: '#374151' }}>{idx + 1}</span>
              <button
                onClick={() => onAdvance(item)}
                disabled={liChanging === item.id}
                style={{ padding: '3px 10px', border: 'none', borderRadius: 12, backgroundColor: liColors.bg, color: liColors.text, fontWeight: 600, fontSize: 11, cursor: 'pointer', textTransform: 'capitalize' }}
                title="Click to advance status"
              >
                {item.status.replace('_', ' ')}
              </button>
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                <tr>
                  <td style={s.rollerLabel}>ROOM/WINDOW</td>
                  <td style={s.rollerValue}>{item.description}</td>
                </tr>
                <tr>
                  <td style={s.rollerLabel}>QUANTITY</td>
                  <td style={s.rollerValue}>{Number(item.quantity)}</td>
                </tr>
                {AUSTRIAN_SHADE_DISPLAY_FIELDS.map(([parseKey, displayLabel]) => (
                  <tr key={parseKey}>
                    <td style={s.rollerLabel}>{displayLabel}</td>
                    <td style={s.rollerValue}>{parsed[parseKey] || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
}

// Balloon Shade shares the identical shadeRowToArgs notes shape as Austrian
// Shade (CreateWorkOrder.tsx) — same field set, own list-view component so
// it's no longer misrouted onto the drapery-shaped generic TravelerItems.
const BALLOON_SHADE_DISPLAY_FIELDS: [string, string][] = AUSTRIAN_SHADE_DISPLAY_FIELDS;

function BalloonShadeItems({ items, liChanging, onAdvance }: { items: LineItem[]; liChanging: number | null; onAdvance: (item: LineItem) => void }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
      {items.map((item, idx) => {
        const parsed = parseNotes(item.notes);
        const liColors = liStatusColors[item.status] || liStatusColors.pending;
        return (
          <div
            key={item.id}
            style={{ border: '1px solid #d1d5db', borderRadius: 6, backgroundColor: item.status === 'completed' ? '#f0fdf4' : '#fff', padding: '14px 16px', minWidth: 280, flex: '1 1 280px', maxWidth: 340 }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <span style={{ fontWeight: 700, fontSize: 18, color: '#374151' }}>{idx + 1}</span>
              <button
                onClick={() => onAdvance(item)}
                disabled={liChanging === item.id}
                style={{ padding: '3px 10px', border: 'none', borderRadius: 12, backgroundColor: liColors.bg, color: liColors.text, fontWeight: 600, fontSize: 11, cursor: 'pointer', textTransform: 'capitalize' }}
                title="Click to advance status"
              >
                {item.status.replace('_', ' ')}
              </button>
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                <tr>
                  <td style={s.rollerLabel}>ROOM/WINDOW</td>
                  <td style={s.rollerValue}>{item.description}</td>
                </tr>
                <tr>
                  <td style={s.rollerLabel}>QUANTITY</td>
                  <td style={s.rollerValue}>{Number(item.quantity)}</td>
                </tr>
                {BALLOON_SHADE_DISPLAY_FIELDS.map(([parseKey, displayLabel]) => (
                  <tr key={parseKey}>
                    <td style={s.rollerLabel}>{displayLabel}</td>
                    <td style={s.rollerValue}>{parsed[parseKey] || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
}

// Pleated/Tailored/Shirred Valance explode into one row per fabric-type
// draft_quote_object_item (objectToTravelerRows in CreateWorkOrder.tsx),
// same shape as the generic drapery TravelerItems table; Roman Shade/Valance
// use the same mechanism but collapse down to Fabric + Trim rows only — so
// these three reuse a Fabric/Color + Yds table layout, unlike Austrian/
// Balloon Shade's single-row-per-object card layout, with their own
// notes-key column lists matching travelerRowToArgs exactly (mirrors
// TRAVELER_DISPLAY_COLS' { label, key } shape).
const ROMAN_SHADE_DISPLAY_COLS: { label: string; key: string | null }[] = [
  { label: 'Fabric/Color', key: null },
  { label: 'Yds', key: null },
  { label: 'Qty', key: 'Qty' },
  { label: '# Prs', key: 'Prs' },
  { label: 'F.W.', key: 'F.W.' },
  { label: 'F.L.', key: 'F.L.' },
  { label: 'Line', key: 'Line' },
  { label: 'Inter-Line', key: 'Inter-Line' },
  { label: 'Room/Window', key: 'Room/Window' },
  { label: 'Operating System', key: 'Operating System' },
  { label: 'Board', key: 'Board' },
  { label: 'Returns', key: 'Returns' },
  { label: 'Flap', key: 'Flap' },
  { label: 'Status', key: null },
];

// Roman Valance has no operating system/motor line at all (see CLAUDE.md),
// so no Motor column — otherwise identical to Roman Shade's column set.
const ROMAN_VALANCE_DISPLAY_COLS: { label: string; key: string | null }[] = [
  { label: 'Fabric/Color', key: null },
  { label: 'Yds', key: null },
  { label: 'Qty', key: 'Qty' },
  { label: '# Prs', key: 'Prs' },
  { label: 'F.W.', key: 'F.W.' },
  { label: 'F.L.', key: 'F.L.' },
  { label: 'Line', key: 'Line' },
  { label: 'Inter-Line', key: 'Inter-Line' },
  { label: 'Room/Window', key: 'Room/Window' },
  { label: 'Board', key: 'Board' },
  { label: 'Returns', key: 'Returns' },
  { label: 'Flap', key: 'Flap' },
  { label: 'Status', key: null },
];

// Pleated Valance has Returns instead of Flap, and (like Roman Valance) no
// Motor column. Reused verbatim for Tailored Valance and Shirred Valance,
// same as CreateWorkOrder.tsx's pleatedValanceTravelerCalc.
const PLEATED_VALANCE_DISPLAY_COLS: { label: string; key: string | null }[] = [
  { label: 'Fabric/Color', key: null },
  { label: 'Yds', key: null },
  { label: 'Qty', key: 'Qty' },
  { label: 'F.W.', key: 'F.W.' },
  { label: 'F.L.', key: 'F.L.' },
  { label: 'Line', key: 'Line' },
  { label: 'Inter-Line', key: 'Inter-Line' },
  { label: 'Room/Window', key: 'Room/Window' },
  { label: 'Board', key: 'Board' },
  { label: 'Returns', key: 'Returns' },
  { label: 'Status', key: null },
];

function ShadeTravelerTable({ items, liChanging, onAdvance, cols }: { items: LineItem[]; liChanging: number | null; onAdvance: (item: LineItem) => void; cols: { label: string; key: string | null }[] }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ borderCollapse: 'collapse', fontSize: 13, width: '100%', minWidth: 900 }}>
        <thead>
          <tr>
            {cols.map((col) => (
              <th key={col.label} style={s.th}>{col.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map((item) => {
            const parsed = parseNotes(item.notes);
            const liColors = liStatusColors[item.status] || liStatusColors.pending;
            return (
              <tr key={item.id} style={{ backgroundColor: item.status === 'completed' ? '#f0fdf4' : undefined }}>
                <td style={s.td}>{item.description}</td>
                <td style={{ ...s.td, textAlign: 'center' }}>{Number(item.quantity)}</td>
                {cols.slice(2, -1).map((col) => (
                  <td key={col.label} style={{ ...s.td, textAlign: 'center' }}>{(col.key && parsed[col.key]) || '—'}</td>
                ))}
                <td className="no-print" style={{ ...s.td, textAlign: 'center' }}>
                  <button
                    onClick={() => onAdvance(item)}
                    disabled={liChanging === item.id}
                    style={{ padding: '3px 10px', border: 'none', borderRadius: 12, backgroundColor: liColors.bg, color: liColors.text, fontWeight: 600, fontSize: 11, cursor: 'pointer', whiteSpace: 'nowrap', textTransform: 'capitalize' }}
                    title="Click to advance status"
                  >
                    {item.status.replace('_', ' ')}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function RomanShadeItems({ items, liChanging, onAdvance }: { items: LineItem[]; liChanging: number | null; onAdvance: (item: LineItem) => void }) {
  return <ShadeTravelerTable items={items} liChanging={liChanging} onAdvance={onAdvance} cols={ROMAN_SHADE_DISPLAY_COLS} />;
}

function RomanValanceItems({ items, liChanging, onAdvance }: { items: LineItem[]; liChanging: number | null; onAdvance: (item: LineItem) => void }) {
  return <ShadeTravelerTable items={items} liChanging={liChanging} onAdvance={onAdvance} cols={ROMAN_VALANCE_DISPLAY_COLS} />;
}

function PleatedValanceItems({ items, liChanging, onAdvance }: { items: LineItem[]; liChanging: number | null; onAdvance: (item: LineItem) => void }) {
  return <ShadeTravelerTable items={items} liChanging={liChanging} onAdvance={onAdvance} cols={PLEATED_VALANCE_DISPLAY_COLS} />;
}

const BOX_SPRING_DISPLAY_FIELDS: [string, string][] = [
  ['Bed Size', 'BED SIZE'],
  ['Fabric',   'FABRIC'],
  ['L/W/H',    'BOX SPRING L/W/H'],
];

function BoxSpringItems({ items, liChanging, onAdvance }: { items: LineItem[]; liChanging: number | null; onAdvance: (item: LineItem) => void }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
      {items.map((item, idx) => {
        const parsed = parseNotes(item.notes);
        const liColors = liStatusColors[item.status] || liStatusColors.pending;
        return (
          <div
            key={item.id}
            style={{ border: '1px solid #d1d5db', borderRadius: 6, backgroundColor: item.status === 'completed' ? '#f0fdf4' : '#fff', padding: '14px 16px', minWidth: 280, flex: '1 1 280px', maxWidth: 340 }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <span style={{ fontWeight: 700, fontSize: 18, color: '#374151' }}>{idx + 1}</span>
              <button
                onClick={() => onAdvance(item)}
                disabled={liChanging === item.id}
                style={{ padding: '3px 10px', border: 'none', borderRadius: 12, backgroundColor: liColors.bg, color: liColors.text, fontWeight: 600, fontSize: 11, cursor: 'pointer', textTransform: 'capitalize' }}
                title="Click to advance status"
              >
                {item.status.replace('_', ' ')}
              </button>
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                <tr>
                  <td style={s.rollerLabel}>ROOM/WINDOW</td>
                  <td style={s.rollerValue}>{item.description}</td>
                </tr>
                <tr>
                  <td style={s.rollerLabel}>QUANTITY</td>
                  <td style={s.rollerValue}>{Number(item.quantity)}</td>
                </tr>
                {BOX_SPRING_DISPLAY_FIELDS.map(([parseKey, displayLabel]) => (
                  <tr key={parseKey}>
                    <td style={s.rollerLabel}>{displayLabel}</td>
                    <td style={s.rollerValue}>{parsed[parseKey] || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
}

const DUVET_DISPLAY_FIELDS: [string, string][] = [
  ['Size',      'SIZE'],
  ['Fabric',    'FABRIC'],
  ['Self Welt', 'SELF WELT'],
];

function DuvetItems({ items, liChanging, onAdvance }: { items: LineItem[]; liChanging: number | null; onAdvance: (item: LineItem) => void }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
      {items.map((item, idx) => {
        const parsed = parseNotes(item.notes);
        const liColors = liStatusColors[item.status] || liStatusColors.pending;
        return (
          <div
            key={item.id}
            style={{ border: '1px solid #d1d5db', borderRadius: 6, backgroundColor: item.status === 'completed' ? '#f0fdf4' : '#fff', padding: '14px 16px', minWidth: 280, flex: '1 1 280px', maxWidth: 340 }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <span style={{ fontWeight: 700, fontSize: 18, color: '#374151' }}>{idx + 1}</span>
              <button
                onClick={() => onAdvance(item)}
                disabled={liChanging === item.id}
                style={{ padding: '3px 10px', border: 'none', borderRadius: 12, backgroundColor: liColors.bg, color: liColors.text, fontWeight: 600, fontSize: 11, cursor: 'pointer', textTransform: 'capitalize' }}
                title="Click to advance status"
              >
                {item.status.replace('_', ' ')}
              </button>
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                <tr>
                  <td style={s.rollerLabel}>ROOM/WINDOW</td>
                  <td style={s.rollerValue}>{item.description}</td>
                </tr>
                <tr>
                  <td style={s.rollerLabel}>QUANTITY</td>
                  <td style={s.rollerValue}>{Number(item.quantity)}</td>
                </tr>
                {DUVET_DISPLAY_FIELDS.map(([parseKey, displayLabel]) => (
                  <tr key={parseKey}>
                    <td style={s.rollerLabel}>{displayLabel}</td>
                    <td style={s.rollerValue}>{parsed[parseKey] || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
}

const COVERLET_DISPLAY_FIELDS: [string, string][] = [
  ['Size',   'SIZE'],
  ['Fabric', 'FABRIC'],
];

function CoverletItems({ items, liChanging, onAdvance }: { items: LineItem[]; liChanging: number | null; onAdvance: (item: LineItem) => void }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
      {items.map((item, idx) => {
        const parsed = parseNotes(item.notes);
        const liColors = liStatusColors[item.status] || liStatusColors.pending;
        return (
          <div
            key={item.id}
            style={{ border: '1px solid #d1d5db', borderRadius: 6, backgroundColor: item.status === 'completed' ? '#f0fdf4' : '#fff', padding: '14px 16px', minWidth: 280, flex: '1 1 280px', maxWidth: 340 }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <span style={{ fontWeight: 700, fontSize: 18, color: '#374151' }}>{idx + 1}</span>
              <button
                onClick={() => onAdvance(item)}
                disabled={liChanging === item.id}
                style={{ padding: '3px 10px', border: 'none', borderRadius: 12, backgroundColor: liColors.bg, color: liColors.text, fontWeight: 600, fontSize: 11, cursor: 'pointer', textTransform: 'capitalize' }}
                title="Click to advance status"
              >
                {item.status.replace('_', ' ')}
              </button>
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                <tr>
                  <td style={s.rollerLabel}>ROOM/WINDOW</td>
                  <td style={s.rollerValue}>{item.description}</td>
                </tr>
                <tr>
                  <td style={s.rollerLabel}>QUANTITY</td>
                  <td style={s.rollerValue}>{Number(item.quantity)}</td>
                </tr>
                {COVERLET_DISPLAY_FIELDS.map(([parseKey, displayLabel]) => (
                  <tr key={parseKey}>
                    <td style={s.rollerLabel}>{displayLabel}</td>
                    <td style={s.rollerValue}>{parsed[parseKey] || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
}

const TABLECLOTH_ROUND_DISPLAY_FIELDS: [string, string][] = [
  ['Size',   'SIZE'],
  ['Fabric', 'FABRIC'],
];

function TableclothRoundItems({ items, liChanging, onAdvance }: { items: LineItem[]; liChanging: number | null; onAdvance: (item: LineItem) => void }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
      {items.map((item, idx) => {
        const parsed = parseNotes(item.notes);
        const liColors = liStatusColors[item.status] || liStatusColors.pending;
        return (
          <div
            key={item.id}
            style={{ border: '1px solid #d1d5db', borderRadius: 6, backgroundColor: item.status === 'completed' ? '#f0fdf4' : '#fff', padding: '14px 16px', minWidth: 280, flex: '1 1 280px', maxWidth: 340 }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <span style={{ fontWeight: 700, fontSize: 18, color: '#374151' }}>{idx + 1}</span>
              <button
                onClick={() => onAdvance(item)}
                disabled={liChanging === item.id}
                style={{ padding: '3px 10px', border: 'none', borderRadius: 12, backgroundColor: liColors.bg, color: liColors.text, fontWeight: 600, fontSize: 11, cursor: 'pointer', textTransform: 'capitalize' }}
                title="Click to advance status"
              >
                {item.status.replace('_', ' ')}
              </button>
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                <tr>
                  <td style={s.rollerLabel}>ROOM/WINDOW</td>
                  <td style={s.rollerValue}>{item.description}</td>
                </tr>
                <tr>
                  <td style={s.rollerLabel}>QUANTITY</td>
                  <td style={s.rollerValue}>{Number(item.quantity)}</td>
                </tr>
                {TABLECLOTH_ROUND_DISPLAY_FIELDS.map(([parseKey, displayLabel]) => (
                  <tr key={parseKey}>
                    <td style={s.rollerLabel}>{displayLabel}</td>
                    <td style={s.rollerValue}>{parsed[parseKey] || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
}

const TABLECLOTH_RECTANGLE_DISPLAY_FIELDS: [string, string][] = [
  ['Size',   'SIZE'],
  ['Fabric', 'FABRIC'],
];

function TableclothRectangleItems({ items, liChanging, onAdvance }: { items: LineItem[]; liChanging: number | null; onAdvance: (item: LineItem) => void }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
      {items.map((item, idx) => {
        const parsed = parseNotes(item.notes);
        const liColors = liStatusColors[item.status] || liStatusColors.pending;
        return (
          <div
            key={item.id}
            style={{ border: '1px solid #d1d5db', borderRadius: 6, backgroundColor: item.status === 'completed' ? '#f0fdf4' : '#fff', padding: '14px 16px', minWidth: 280, flex: '1 1 280px', maxWidth: 340 }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <span style={{ fontWeight: 700, fontSize: 18, color: '#374151' }}>{idx + 1}</span>
              <button
                onClick={() => onAdvance(item)}
                disabled={liChanging === item.id}
                style={{ padding: '3px 10px', border: 'none', borderRadius: 12, backgroundColor: liColors.bg, color: liColors.text, fontWeight: 600, fontSize: 11, cursor: 'pointer', textTransform: 'capitalize' }}
                title="Click to advance status"
              >
                {item.status.replace('_', ' ')}
              </button>
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                <tr>
                  <td style={s.rollerLabel}>ROOM/WINDOW</td>
                  <td style={s.rollerValue}>{item.description}</td>
                </tr>
                <tr>
                  <td style={s.rollerLabel}>QUANTITY</td>
                  <td style={s.rollerValue}>{Number(item.quantity)}</td>
                </tr>
                {TABLECLOTH_RECTANGLE_DISPLAY_FIELDS.map(([parseKey, displayLabel]) => (
                  <tr key={parseKey}>
                    <td style={s.rollerLabel}>{displayLabel}</td>
                    <td style={s.rollerValue}>{parsed[parseKey] || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  hCell: {
    flex: 1,
    padding: '10px 16px',
    borderRight: '1px solid #374151',
  },
  hLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: 2,
  },
  hValue: {
    fontSize: 15,
    fontWeight: 600,
    color: '#111827',
  },
  th: {
    padding: '8px 8px',
    border: '1px solid #374151',
    backgroundColor: '#f9fafb',
    fontWeight: 600,
    color: '#374151',
    whiteSpace: 'nowrap',
    textAlign: 'center',
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  td: {
    padding: '10px 8px',
    border: '1px solid #e5e7eb',
    color: '#111827',
    fontSize: 13,
  },
  rollerLabel: {
    fontSize: 11,
    fontWeight: 700,
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    textAlign: 'right',
    paddingRight: 12,
    paddingBottom: 7,
    width: '48%',
    whiteSpace: 'nowrap',
  },
  rollerValue: {
    fontSize: 13,
    color: '#111827',
    paddingBottom: 7,
    borderBottom: '1px solid #f3f4f6',
  },
};
